import { trenneBeraterRegelkontext } from '../../utils/berater-regelanzeige';
import { Schnellchat } from '../Schnellchat';
import { BildEinfuegeZone } from '../BildEinfuegeZone';
import { kopfAusTaetigkeit, type GemesseneTaetigkeit } from './cui/chatKopfTaetigkeit';
import { useState, useRef, useEffect, useCallback, memo } from 'react';
import type React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ACCOUNTS, accountAssistantLabel, accountAccessLabel } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { entwurfSchluessel, leseEntwurf, schreibeEntwurf } from '../../lib/entwuerfe';
import QueueOverlay from './QueueOverlay';
import { VoiceMicButton } from './VoiceMicButton';
import { validateApiResponse } from '../../lib/validateApiResponse';
import { usage } from '../../lib/cuiUsage';
import { logCuiTelemetry } from '../../lib/cuiTelemetry';
import { AskUserQuestionBlock, type AskUserQuestionItem } from './cui/AskUserQuestionBlock';
import { merkeNachrichtenCache } from '../../lib/browserSpeicher';
import { VerdichtungsBlock, istVerdichtungsZusammenfassung } from './cui/VerdichtungsBlock';
import { uebernahmeHaengt, type Zustellstand } from './cui/uebernahmeHaengt';

// --- Types ---
interface AgentSessionState {
  accountId?: string;
  state: 'idle' | 'working' | 'needs_attention';
  reason?: string;
}

const SWITCHABLE_ACCOUNTS = ACCOUNTS.filter(a => a.id !== 'local');

/**
 * Geduld beim Senden. /api/mission/send antwortet sofort, wenn der CLI-Prozess
 * der Sitzung noch lebt (Nachricht wird durchgereicht) — muss er erst neu
 * gestartet und die Sitzung fortgesetzt werden, dauert es deutlich laenger.
 * Laeuft die Zeit ab, heisst das ausdruecklich NICHT "nicht angekommen":
 * siehe confirmDelivery() und den catch-Zweig in handleSend.
 */
const SEND_TIMEOUT_MS = 65_000;

// Wartezeiten fuer den automatischen Retry, wenn der Server den sendsInFlight-
// Lock haelt (409 duplicate_send_rejected, z.B. ein zweites Fenster oder eine
// Maschine schreibt gerade in dieselbe Session). Gleiche Werte wie
// GuidePanel.zustellenAn (WARTEN_MS), das denselben 409 fuer Maschinen-Sends
// schon so behandelt statt die Antwort nach einem Versuch zu verlieren.
const SEND_LOCK_RETRY_MS = [4000, 12000];

/**
 * MEMORY FIX: Maximum messages kept in component state. Prevents unbounded
 * growth when system messages (errors, reconnect notices) accumulate faster
 * than polls can reset them. 200 is generous — the UI only shows last 15
 * by default (showAllMessages toggles to full).
 */
const MAX_MESSAGES = 200;
const trimMessages = (msgs: Message[]): Message[] =>
  msgs.length > MAX_MESSAGES ? msgs.slice(-MAX_MESSAGES) : msgs;

interface ContentBlock {
  type: string;
  text?: string;
  name?: string;
  id?: string;
  input?: Record<string, unknown>;
}

interface Message {
  role: 'user' | 'assistant' | 'system' | 'rate_limit' | 'api_error';
  content: string | ContentBlock[];
  timestamp?: string;
  _streaming?: boolean;
  /** Vom Server gesetzt: automatische Verdichtungs-Zusammenfassung (eingeklappt anzeigen). */
  verdichtung?: boolean;
  /** Nur bei 'rate_limit': der Zugang, dem die Sperre gehoert — NICHT zwingend der,
   *  auf dem das Panel gerade steht. Fehlt, wenn der Server sie nicht eindeutig
   *  zuordnen konnte (siehe attributeLimitMessage in shared/account-limits.ts). */
  accountId?: string;
  /** Nur bei 'rate_limit': Epoch-ms, ab wann der Zugang wieder annimmt. */
  resetsAt?: number;
}

interface Permission {
  id: string;
  type: string;
  toolName?: string;
  title?: string;
  toolInput?: Record<string, unknown>;
}

interface CuiLitePanelProps {
  buehneId?: string;
  accountId?: string;
  projectId?: string;
  workDir?: string;
  panelId?: string;
  isTabVisible?: boolean;
  onRouteChange?: (route: string) => void;
  initialRoute?: string;
  initialSessionId?: string;
  onLoadFailed?: (sessionId: string) => void;
  onLoadSucceeded?: () => void;
  onLoadError?: (reason: string) => void;
  /** Retry reading without discarding the displayed conversation or draft. */
  refreshToken?: number;
  /** Wird vor dem Senden mit dem eingegebenen Text gefragt; `true` heisst: verbraucht (z. B. ein Ansicht-Kurzbefehl), es wird NICHT gesendet. Nur fuer die Tipp-Eingabe, nie fuer Overrides. */
  vorSenden?: (text: string) => boolean;
  /** Legt einen Text zurueck ins Eingabefeld (nie automatisch senden); wirkt je `nr` einmal. */
  eingabeVorgabe?: { nr: number; text: string };
  onFinish?: (sessionId: string) => void;
  onStateChange?: (state: 'idle' | 'working' | 'needs_attention') => void;
  compactInputBar?: boolean;
  /** Einfache Ansicht fuer den Sales-Bereich. Zwei Wirkungen:
   *  (a) ein Knopf zurueck zur Chat-Liste — im Arbeitsbereich ist jedes Panel
   *      EIN Chat und die Liste haengt an den Tabs, hier gibt es nur dieses
   *      eine Panel, und ohne den Knopf kaeme man nie wieder an frueheres;
   *  (b) keine Konto-Auswahl — sie bot einem Vertriebspartner die Konten
   *      anderer Leute an (Sahori, Kurt) und hat fuer ihn keine Bedeutung. */
  simpleMode?: boolean;
  /**
   * Schlanke Darstellung fuer die Guide-Spalte: Verlauf und Eingabefeld, sonst
   * nichts. Rafael am 04.09. ueber die Spaltenchats: "das ist eine normale
   * Chat-Session. Es muss irgendwie eine extra Session sein, die ein bisschen
   * kompakter ist, vor allem nicht unten mit all diesem ganzen blending."
   *
   * Ausgeblendet wird Werkstatt-Bedienung, die in den Arbeitsbereich gehoert:
   * Konto-Auswahl, Live/Refresh/Review/Dim/Pause/Finish, Modell- und LITE-Marke,
   * Plan/Loop/Stop/KILL, die Kurzprompt-Kacheln und das Diktat.
   *
   * BLEIBEN MUSS (Rafael 04.09., nachdem der erste Zuschnitt zu scharf war):
   *   · Anhang (📎) und Einfuege-Feld (🖼) — "die Bild-einfuege-Funktion, die musst
   *     du auf jeden Fall anzeigen. Das ist ja eher eine der wichtigsten." Darueber
   *     schickt er die Screenshots, aus denen diese Befunde ueberhaupt entstehen;
   *     ohne sie ist der Rueckkanal zu.
   *   · Aufmerksamkeits-Banner und Freigabe-Leiste — das ist Gespraech, nicht
   *     Werkstatt: ohne sie saehe er nicht, dass eine Rueckfrage wartet.
   * In der Spalte steht damit: Verlauf, Eingabe, Bild einfuegen. Sonst nichts.
   */
  kompakt?: boolean;
  /**
   * Titel fuer die Kopfzeile, wenn der Aufrufer einen besseren hat als den
   * Konversationsnamen (Coach-Spalte: „Dev — Coach"). Der Aufrufer zeichnet
   * dann KEINE eigene Kopfzeile mehr darueber — eine Kopfzeile, ein Titel.
   */
  titel?: string;
  /** Zusaetzliche Eintraege oben im ⋯-Menue (z. B. „Sitzung neu starten" der Coach-Spalte). */
  menueZusatz?: React.ReactNode;
  /** Kleines Bedienelement rechts in der Kopfzeile (z. B. „Ansicht" im eigenstaendigen Coach-Fenster). */
  kopfZusatz?: React.ReactNode;
}

// --- Markdown Components (Tokyo Night) ---
const markdownComponents = {
  h1: ({ ...props }) => <h1 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--tn-text)', marginTop: '16px', marginBottom: '8px' }} {...props} />,
  h2: ({ ...props }) => <h2 style={{ fontSize: '17px', fontWeight: '600', color: 'var(--tn-text)', marginTop: '12px', marginBottom: '6px' }} {...props} />,
  h3: ({ ...props }) => <h3 style={{ fontSize: '15px', fontWeight: '600', color: 'var(--tn-blue)', marginTop: '10px', marginBottom: '5px' }} {...props} />,
  p: ({ ...props }) => <p style={{ marginBottom: '8px', lineHeight: '1.6' }} {...props} />,
  ul: ({ ...props }) => <ul style={{ marginLeft: '16px', marginBottom: '8px', listStyleType: 'disc' }} {...props} />,
  ol: ({ ...props }) => <ol style={{ marginLeft: '16px', marginBottom: '8px' }} {...props} />,
  li: ({ ...props }) => <li style={{ marginBottom: '3px' }} {...props} />,
  code: ({ className, children, ...props }: React.ComponentPropsWithoutRef<'code'>) => {
    // react-markdown v10: inline code has no className, block code gets className="language-xxx"
    const isBlock = !!className;
    if (!isBlock) {
      return <code style={{ background: 'var(--tn-bg-highlight)', padding: '2px 5px', borderRadius: '3px', fontSize: '13px', fontFamily: 'monospace', color: 'var(--tn-cyan)' }} {...props}>{children}</code>;
    }
    const lang = className?.replace('language-', '') || '';
    return (
      <div style={{ position: 'relative', marginBottom: '10px' }}>
        {lang && <span style={{ position: 'absolute', top: 4, right: 8, fontSize: '10px', color: 'var(--tn-text-muted)', textTransform: 'uppercase' }}>{lang}</span>}
        <code style={{ display: 'block', background: 'var(--tn-bg-dark)', padding: '12px', borderRadius: '6px', fontSize: '13px', fontFamily: 'monospace', overflow: 'auto', border: '1px solid var(--tn-border)', lineHeight: '1.5' }} {...props}>{children}</code>
      </div>
    );
  },
  pre: ({ children, ...props }: React.ComponentPropsWithoutRef<'pre'>) => <pre style={{ margin: 0 }} {...props}>{children}</pre>,
  table: ({ ...props }) => <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '12px', fontSize: '13px' }} {...props} />,
  thead: ({ ...props }) => <thead style={{ background: 'var(--tn-bg-highlight)' }} {...props} />,
  th: ({ ...props }) => <th style={{ padding: '8px 10px', textAlign: 'left' as const, fontWeight: '600', borderBottom: '1px solid var(--tn-border)' }} {...props} />,
  td: ({ ...props }) => <td style={{ padding: '6px 10px', borderBottom: '1px solid var(--tn-border)' }} {...props} />,
  blockquote: ({ ...props }) => <blockquote style={{ borderLeft: '3px solid var(--tn-blue)', paddingLeft: '12px', marginBottom: '8px', color: 'var(--tn-text-muted)', fontStyle: 'italic' }} {...props} />,
  a: ({ ...props }) => <a style={{ color: 'var(--tn-blue)', textDecoration: 'none' }} {...props} />,
  strong: ({ ...props }) => <strong style={{ fontWeight: '600', color: 'var(--tn-text)' }} {...props} />,
  hr: ({ ...props }) => <hr style={{ border: 'none', borderTop: '1px solid var(--tn-border)', margin: '12px 0' }} {...props} />,
};

// --- Tool Use Block (interactive) ---
function ToolUseBlock({ block, onRespond, workDir, serverPlanText, sessionCwd, expandByDefault, blockId }: { block: ContentBlock; onRespond?: (text: string) => void; workDir?: string; serverPlanText?: string; sessionCwd?: string; expandByDefault?: boolean; blockId: string }) {
  const [planText, setPlanText] = useState<string | null>(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [responded, setResponded] = useState(false);
  const [expanded, setExpanded] = useState(() => expandByDefault ?? false);
  useEffect(() => { setExpanded(expandByDefault ?? false); }, [expandByDefault]);

  // Load plan text: prefer server-provided, then inline input, then file fallback
  useEffect(() => {
    if (block.name !== 'ExitPlanMode' || planText !== null) return;
    // 1. Server already read the plan file for us
    if (serverPlanText && serverPlanText.trim()) {
      setPlanText(serverPlanText);
      return;
    }
    // 2. Plan content in tool_use input (future-proof)
    const inlinePlan = block.input?.plan as string | undefined;
    if (inlinePlan && inlinePlan.trim()) {
      setPlanText(inlinePlan);
      return;
    }
    // 3. Fallback: load from file using sessionCwd (actual session CWD) or workDir (project prop)
    const effectiveDir = sessionCwd || workDir;
    if (!effectiveDir) { setPlanText(''); return; }
    setPlanLoading(true);
    if (window.__cuiServerAlive === false) { setPlanLoading(false); return; }
    fetch(`/api/file-read?path=${encodeURIComponent(effectiveDir + '/.claude/plan.md')}`, { signal: AbortSignal.timeout(5000) })
      .then(r => r.ok ? r.text() : Promise.reject('not found'))
      .then(text => setPlanText(text))
      .catch((err) => { if (err !== 'not found') console.warn('[CuiLite] Load plan error:', err); setPlanText(''); })
      .finally(() => setPlanLoading(false));
  }, [block.name, workDir, sessionCwd, serverPlanText, planText]);

  if (block.name === 'AskUserQuestion') {
    /*
     * AUSWAEHLEN, DANN ABSENDEN — die Mechanik liegt jetzt in
     * `AskUserQuestionBlock`, gemeinsam mit dem zweiten Panel. Hier stand
     * vorher eine von zwei wortgleichen Kopien desselben Renderers, in der
     * jeder Options-Klick direkt `onRespond` aufrief.
     */
    return (
      <AskUserQuestionBlock
        questions={(block.input?.questions || []) as AskUserQuestionItem[]}
        onRespond={onRespond}
        aiIdPrefix="cui-lite"
        rawInput={block.input}
      />
    );
  }

  if (block.name === 'ExitPlanMode') {
    const allowedPrompts = block.input?.allowedPrompts as Array<{ tool: string; prompt: string }> | undefined;
    const handleClick = (text: string) => {
      if (responded) return;
      setResponded(true);
      onRespond?.(text);
    };

    return (
      <div style={{ margin: '8px 0', padding: '10px 14px', background: 'rgba(245,158,11,0.08)', borderRadius: 6, border: '1px solid rgba(245,158,11,0.2)' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tn-warn)', marginBottom: 8 }}>
          Plan bereit zur Freigabe
        </div>
        {/* Plan text from .claude/plan.md */}
        {planLoading && <div style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginBottom: 8 }}>Plan wird geladen...</div>}
        {planText && (
          <details data-ai-id="cui-lite-plan-details" style={{ marginBottom: 8 }} open>
            <summary style={{ fontSize: 11, color: 'var(--tn-warn)', cursor: 'pointer', marginBottom: 4 }}>
              Plan anzeigen ({planText.length > 1000 ? `${Math.round(planText.length / 1000)}k Zeichen` : `${planText.length} Zeichen`})
            </summary>
            <div style={{ padding: '8px 10px', borderRadius: 4, background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', fontSize: 12, lineHeight: 1.6, maxHeight: 300, overflow: 'auto', color: 'var(--tn-text)' }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{planText}</ReactMarkdown>
            </div>
          </details>
        )}
        {/* Permissions */}
        {allowedPrompts && allowedPrompts.length > 0 && (
          <details data-ai-id="cui-lite-plan-permissions-details" style={{ marginBottom: 8 }}>
            <summary style={{ fontSize: 11, color: 'var(--tn-text-muted)', cursor: 'pointer', marginBottom: 4 }}>
              Berechtigungen ({allowedPrompts.length})
            </summary>
            <div style={{ padding: '6px 8px', borderRadius: 4, background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', fontSize: 11, lineHeight: 1.5 }}>
              {allowedPrompts.map((p, i) => (
                <div key={i} style={{ color: 'var(--tn-text-muted)', marginBottom: 2 }}>
                  <span style={{ color: 'var(--tn-text)', fontWeight: 600 }}>{p.tool}</span>: {p.prompt}
                </div>
              ))}
            </div>
          </details>
        )}
        {responded ? (
          <div style={{ fontSize: 12, color: 'var(--tn-ok)', fontWeight: 600, padding: '6px 0' }}>
            Freigegeben — wird ausgefuehrt...
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              data-ai-id="cui-lite-plan-approve"
              onClick={() => handleClick('yes')}
              style={{ padding: '6px 16px', borderRadius: 4, cursor: 'pointer', background: 'var(--tn-ok)', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600 }}
            >
              Freigeben
            </button>
            <button
              data-ai-id="cui-lite-plan-reject"
              onClick={() => handleClick('no, please revise the plan')}
              style={{ padding: '6px 16px', borderRadius: 4, cursor: 'pointer', background: 'var(--tn-fehler)', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600 }}
            >
              Ablehnen
            </button>
          </div>
        )}
      </div>
    );
  }

  if (block.name === 'EnterPlanMode') {
    return (
      <div style={{ margin: '8px 0', padding: '10px 14px', background: 'rgba(245,158,11,0.08)', borderRadius: 6, border: '1px solid rgba(245,158,11,0.2)' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tn-warn)', marginBottom: 8 }}>
          Claude moechte in den Plan-Modus wechseln
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            data-ai-id="cui-lite-enter-plan-approve"
            onClick={() => onRespond?.('yes')}
            style={{ padding: '6px 16px', borderRadius: 4, cursor: 'pointer', background: 'var(--tn-ok)', border: 'none', color: '#fff', fontSize: 12, fontWeight: 600 }}
          >
            OK
          </button>
          <button
            data-ai-id="cui-lite-enter-plan-reject"
            onClick={() => onRespond?.('no, skip planning')}
            style={{ padding: '6px 16px', borderRadius: 4, cursor: 'pointer', background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', color: 'var(--tn-text)', fontSize: 12, fontWeight: 600 }}
          >
            Nein
          </button>
        </div>
      </div>
    );
  }

  // Other tool_use blocks - show tool name + key detail, expandable on click
  const toolName = block.name || 'tool';
  const inp = block.input || {};
  let detail = '';
  if (toolName === 'Bash' && inp.command) {
    detail = String(inp.command).length > 80 ? String(inp.command).slice(0, 77) + '...' : String(inp.command);
  } else if ((toolName === 'Read' || toolName === 'Write') && inp.file_path) {
    detail = String(inp.file_path).split('/').slice(-2).join('/');
  } else if (toolName === 'Edit' && inp.file_path) {
    detail = String(inp.file_path).split('/').slice(-2).join('/');
  } else if ((toolName === 'Grep' || toolName === 'Glob') && inp.pattern) {
    detail = String(inp.pattern).slice(0, 50);
  } else if (toolName === 'WebSearch' && inp.query) {
    detail = String(inp.query).slice(0, 60);
  } else if (toolName === 'WebFetch' && inp.url) {
    detail = String(inp.url).slice(0, 60);
  } else if (toolName === 'Task' && inp.description) {
    detail = String(inp.description).slice(0, 50);
  } else if (inp.description) {
    detail = String(inp.description).slice(0, 50);
  }

  let expandedContent = '';
  if (toolName === 'Bash' && inp.command) {
    expandedContent = String(inp.command);
  } else if (toolName === 'Edit' && inp.file_path) {
    const parts = [`File: ${inp.file_path}`];
    if (inp.old_string) parts.push(`\n--- remove:\n${String(inp.old_string).slice(0, 400)}`);
    if (inp.new_string) parts.push(`\n+++ add:\n${String(inp.new_string).slice(0, 400)}`);
    expandedContent = parts.join('');
  } else {
    expandedContent = JSON.stringify(inp, null, 2);
  }

  return (
    <div style={{ margin: '4px 0', maxWidth: '100%' }}>
      <div
        data-ai-id={`cui-lite-tool-toggle-${blockId}`}
        onClick={() => setExpanded(e => !e)}
        style={{ padding: '4px 8px', fontSize: 11, color: 'var(--tn-text-muted)', background: 'var(--tn-bg-highlight)', borderRadius: expanded ? '3px 3px 0 0' : 3, display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%', cursor: 'pointer', userSelect: 'none' as const }}
      >
        <span style={{ fontWeight: 600, flexShrink: 0 }}>{toolName}</span>
        {detail && <span style={{ opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{detail}</span>}
        <span style={{ flexShrink: 0, opacity: 0.4, fontSize: 9 }}>{expanded ? '▲' : '▼'}</span>
      </div>
      {expanded && (
        <pre style={{
          margin: 0, padding: '8px 10px',
          fontSize: 11, lineHeight: 1.5,
          background: 'var(--tn-bg-dark)',
          border: '1px solid var(--tn-border)',
          borderTop: 'none',
          borderRadius: '0 0 3px 3px',
          color: 'var(--tn-text)',
          overflow: 'auto',
          maxHeight: 240,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all',
          fontFamily: 'monospace',
        }}>
          {expandedContent}
        </pre>
      )}
    </div>
  );
}

// --- Message Row (memoized: prevents re-rendering 200+ ReactMarkdown instances on poll) ---
const INTERACTIVE_TOOL_NAMES = ['AskUserQuestion', 'ExitPlanMode', 'EnterPlanMode'];

function isUserTextMessage(msg: Message): boolean {
  if (msg.role !== 'user') return false;
  if (typeof msg.content === 'string') return msg.content.trim().length > 0;
  if (Array.isArray(msg.content)) return msg.content.some(b => b.type === 'text' && typeof b.text === 'string' && b.text.trim().length > 0);
  return false;
}

// Index of the last assistant message containing an interactive tool_use that
// has not been answered by a real user-text message after it. The SDK auto-
// cancels AskUserQuestion in headless mode with an is_error tool_result, so
// the question would never be `isLast`. Keep the box visible until the user
// (or auto-inject loop) actually replies with text.
function findLastUnansweredQuestionIdx(messages: Message[]): number {
  let lastUserTextIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (isUserTextMessage(messages[i])) { lastUserTextIdx = i; break; }
  }
  for (let i = messages.length - 1; i > lastUserTextIdx; i--) {
    const m = messages[i];
    if (m.role !== 'assistant' || !Array.isArray(m.content)) continue;
    if (m.content.some((b: ContentBlock) => b.type === 'tool_use' && !!b.name && INTERACTIVE_TOOL_NAMES.includes(b.name))) {
      return i;
    }
  }
  return -1;
}

const MessageRow = memo(function MessageRow({ msg, rowId, onRespond, isLast, forceInteractive, workDir, selectedId, serverPlanText, sessionCwd, expandTools }: { msg: Message; rowId: string; onRespond?: (text: string) => void; isLast: boolean; forceInteractive?: boolean; workDir?: string; selectedId?: string; serverPlanText?: string; sessionCwd?: string; expandTools?: boolean }) {
  const blocks: ContentBlock[] = typeof msg.content === 'string'
    ? [{ type: 'text', text: msg.content }]
    : Array.isArray(msg.content) ? msg.content : [];

  const textBlocks = blocks.filter(b => b.type === 'text');
  const toolUseBlocks = blocks.filter(b => b.type === 'tool_use');

  const showInteractive = (isLast || forceInteractive) && msg.role === 'assistant';
  const interactiveBlocks = showInteractive
    ? toolUseBlocks.filter(b => INTERACTIVE_TOOL_NAMES.includes(b.name || ''))
    : [];
  const infoBlocks = showInteractive
    ? toolUseBlocks.filter(b => !INTERACTIVE_TOOL_NAMES.includes(b.name || ''))
    : toolUseBlocks;

  const originalText = textBlocks.map(b => b.text || '').join('\n');
  const { text, regeln } = msg.role === 'user' ? trenneBeraterRegelkontext(originalText) : { text: originalText, regeln: undefined };
  if (!text.trim() && !regeln && toolUseBlocks.length === 0) return null;

  // Verdichtungs-Zusammenfassung eingeklappt statt als Nutzernachricht (Rafael, 03.10.2026).
  if (msg.role === 'user' && (msg.verdichtung || istVerdichtungsZusammenfassung(text))) {
    return (
      <VerdichtungsBlock text={text} timestamp={msg.timestamp} renderText={t => (
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{t}</ReactMarkdown>
      )} />
    );
  }

  // Streaming partial — render as plain text with pulsing cursor (no Markdown for perf)
  if (msg._streaming) {
    const streamText = typeof msg.content === 'string' ? msg.content : '';
    return (
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--tn-border)' }}>
        <pre style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
          color: 'var(--tn-text-subtle)', lineHeight: 1.6,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
        }}>
          {streamText}
          <span style={{ animation: 'pulse 1s ease-in-out infinite', color: 'var(--tn-violett)' }}>|</span>
        </pre>
      </div>
    );
  }

  // Rate limit and API error messages get special styling
  if (msg.role === 'rate_limit' || msg.role === 'api_error') {
    const isRateLimit = msg.role === 'rate_limit';
    // Die Zeile muss sagen, WEN sie meint. Nach einem Zugangswechsel bleibt sie im
    // Verlauf stehen, und dann steht die Sperre von KI-Zugang 1 rot in einem Fenster,
    // das auf KI-Zugang 3 laeuft und frei ist (Rafael, 19.09.2026). Ohne eindeutige
    // Zuordnung liefert der Server kein accountId — dann bleibt es beim alten Wortlaut,
    // denn ein falscher Name waere schlimmer als gar keiner.
    const betroffenerZugang = msg.accountId ? accountAccessLabel(msg.accountId) : null;
    const freiAb = typeof msg.resetsAt === 'number' && Number.isFinite(msg.resetsAt)
      ? new Date(msg.resetsAt).toISOString().slice(11, 16) + ' UTC'
      : null;
    return (
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--tn-border)', background: isRateLimit ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: isRateLimit ? 'var(--tn-fehler)' : 'var(--tn-warn)' }}>
            {isRateLimit ? 'Rate Limit' : 'API Fehler'}
          </span>
          {betroffenerZugang && (
            <span style={{ fontSize: 11, fontWeight: 700, color: isRateLimit ? 'var(--tn-fehler)' : 'var(--tn-warn)' }}>
              {isRateLimit
                ? `${betroffenerZugang} am Limit${freiAb ? ` · frei ab ${freiAb}` : ''}`
                : betroffenerZugang}
            </span>
          )}
          {msg.timestamp && <span style={{ fontSize: 10, color: 'var(--tn-text-muted)' }}>{new Date(msg.timestamp).toLocaleTimeString()}</span>}
        </div>
        <div style={{ fontSize: 12, color: isRateLimit ? 'var(--tn-fehler)' : 'var(--tn-warn)', lineHeight: 1.5 }}>
          {typeof msg.content === 'string' ? msg.content : isRateLimit ? 'Kontingent ausgelastet. Bitte anderen KI-Zugang waehlen oder auf die Freigabe warten.' : 'API Fehler aufgetreten.'}
        </div>
      </div>
    );
  }

  // Detect if message contains ExitPlanMode to style plan text
  const hasExitPlan = toolUseBlocks.some(b => b.name === 'ExitPlanMode');

  return (
    <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--tn-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{
          fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
          color: msg.role === 'assistant' ? 'var(--tn-green)' : msg.role === 'system' ? 'var(--tn-orange)' : 'var(--tn-blue)',
        }}>
          {msg.role === 'assistant' ? accountAssistantLabel(selectedId) : msg.role === 'system' ? 'System' : 'User'}
        </span>
        {msg.timestamp && (
          <span style={{ fontSize: 10, color: 'var(--tn-text-muted)' }}>
            {new Date(msg.timestamp).toLocaleTimeString()}
          </span>
        )}
      </div>
      {regeln && <details style={{ marginBottom: 8, fontSize: 12, color: 'var(--tn-text-muted)' }}>
        <summary>Interner Regelkontext · keine Nutzernachricht</summary>
        <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{regeln}</pre>
      </details>}
      {text.trim() && (
        <div style={hasExitPlan ? {
          padding: '10px 14px',
          background: 'rgba(245,158,11,0.04)',
          borderRadius: 6,
          border: '1px solid rgba(245,158,11,0.1)',
          marginBottom: 8
        } : undefined}>
          {hasExitPlan && (
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--tn-warn)', textTransform: 'uppercase', marginBottom: 6 }}>
              Plan
            </div>
          )}
          <div style={{ fontSize: 14, color: 'var(--tn-text-subtle)', lineHeight: '1.6' }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {text}
            </ReactMarkdown>
          </div>
        </div>
      )}
      {/* Non-interactive tool_use (past messages or non-interactive tools) */}
      {infoBlocks.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
          {infoBlocks.map((block, i) => (
            <ToolUseBlock key={i} blockId={`${rowId}-info-${i}`} block={block} expandByDefault={expandTools} />
          ))}
        </div>
      )}
      {/* Interactive tool_use blocks (last assistant message only) */}
      {interactiveBlocks.map((block, i) => (
        <ToolUseBlock key={i} blockId={`${rowId}-interactive-${i}`} block={block} onRespond={onRespond} workDir={workDir} serverPlanText={serverPlanText} sessionCwd={sessionCwd} />
      ))}
    </div>
  );
});

// --- Loading state with timeout + retry ---
// Only counts elapsed time when server is alive — pauses during server restarts
function LoadingConversation({ sessionId, onBack, onRetry, onLoadFailed }: { sessionId: string | null; onBack: () => void; onRetry: () => void; onLoadFailed?: (sessionId: string) => void }) {
  const [elapsed, setElapsed] = useState(0);
  const failedRef = useRef(false);
  const retriedOnReconnectRef = useRef(false);
  useEffect(() => {
    const t = setInterval(() => {
      // Pause timeout while server is down — session is likely fine, just can't reach it
      if (window.__cuiServerAlive === false) return;
      setElapsed(s => s + 1);
    }, 1000);
    return () => clearInterval(t);
  }, []);
  // Auto-retry when server comes back alive
  useEffect(() => {
    if (window.__cuiServerAlive === true && !retriedOnReconnectRef.current && elapsed > 0) {
      retriedOnReconnectRef.current = true;
      onRetry();
    }
  });
  useEffect(() => {
    if (elapsed >= 20 && !failedRef.current && onLoadFailed && sessionId) {
      failedRef.current = true;
      onLoadFailed(sessionId);
    }
  }, [elapsed, onLoadFailed, sessionId]);
  const serverDown = window.__cuiServerAlive === false;
  return (
    <div style={{ textAlign: 'center', color: 'var(--tn-text-muted)', marginTop: 40, fontSize: 13 }}>
      {serverDown ? (
        'Server startet neu...'
      ) : elapsed < 15 ? (
        'Lade Konversation...'
      ) : (
        <>
          <div>Konversation konnte nicht geladen werden.</div>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button data-ai-id="cui-lite-load-retry" onClick={() => { failedRef.current = false; setElapsed(0); retriedOnReconnectRef.current = false; onRetry(); }} style={{
              padding: '6px 16px', fontSize: 12, border: '1px solid var(--tn-border)',
              borderRadius: 4, background: 'var(--tn-surface)', color: 'var(--tn-text)', cursor: 'pointer',
            }}>Retry</button>
            <button data-ai-id="cui-lite-load-back" onClick={onBack} style={{
              padding: '6px 16px', fontSize: 12, border: 'none',
              borderRadius: 4, background: 'var(--tn-blue, var(--tn-info))', color: '#fff', cursor: 'pointer',
            }}>Zurueck</button>
          </div>
          {sessionId && <div style={{ marginTop: 8, fontSize: 10, opacity: 0.5, fontFamily: 'monospace' }}>{sessionId.slice(0, 12)}...</div>}
        </>
      )}
    </div>
  );
}

// --- Main Component ---
export default function CuiLitePanel({ buehneId, accountId, projectId, workDir, panelId, isTabVisible = true, onRouteChange, initialSessionId, onLoadFailed, onLoadSucceeded, onLoadError, refreshToken = 0, vorSenden, eingabeVorgabe, onFinish, onStateChange, simpleMode = false, kompakt = false, titel, menueZusatz, kopfZusatz }: CuiLitePanelProps) {
  const { user, authEnabled } = useAuth();
  const [schnellchatOffen, setSchnellchatOffen] = useState(false);
  const isAdvancedUser = !user || user.role === 'admin' || user.role === 'product-owner';
  /* Betreiberwege (Auto-Inject, Taetigkeitsmessung des Guide) sind admin-only und
   * antworten einem Partner IMMER 403. Rafael, 03.10.2026 16:35 UTC, Wahl a: „Konsole
   * ruhig" — fuer Nicht-Admins gar nicht erst anfragen (Vorbild 252bb6409 beim Guide).
   * Bewusst `=== false` statt `!authEnabled`: solange die Anmeldung noch laedt (null),
   * ist nicht entschieden, wer fragt — dann lieber warten als eine 403 erzeugen. */
  const betreiberWege = authEnabled === false || user?.role === 'admin';
  const storageKey = `cui-lite-account-${panelId || projectId || 'default'}`;
  const persistSession = !initialSessionId; // Don't persist to localStorage for AllChats panels

  const getSessionKey = (acctId: string) => `cui-lite-session-${panelId || projectId || 'default'}-${acctId}`;

  /*
   * DIE KONTOWAHL HAENGT AN DER SITZUNG, NICHT AM KASTEN (Rafael, 10.09. 08:24Z:
   * "ich versteh auch nicht warum sich der account immer automatisch auf engelmann
   * zurueckesetzt und ich dann wieder auf gmail umstellen muss - der soll schon da
   * bleiben wo ich ihn einstelle").
   *
   * `storageKey` oben haengt an der Panel-Kennung. Die vergibt die Buehne aus
   * Sitzung UND POSITION im Kartenrezept (shared/guide-kachel-id.ts:
   * `#guide-s-<sid8>-<i>`), und jede Karte bringt ihr eigenes Rezept mit. Steht
   * dieselbe Sitzung auf der naechsten Karte an anderer Stelle, ist die Kennung
   * eine andere, der Schluessel leer — und `config.accountId` aus dem Rezept
   * gewinnt. Dieses Konto ist eine Momentaufnahme aus dem Augenblick, in dem die
   * Karte geschrieben wurde; es hat Rafaels Handwechsel bei jedem Kartenklick
   * ueberschrieben. Sein Waehler sah aus wie eine Einstellung und war eine Anzeige.
   *
   * Die Sitzungskennung ueberlebt Position, Rezept und Neuaufbau. Wer zuletzt
   * geschrieben hat, gewinnt — Rafael am Waehler ODER der Server bei einer
   * Konto-Rotation (conv-account-changed schreibt denselben Schluessel, sonst
   * wuerde eine ausgewichene Sitzung wieder auf das gesperrte Konto zurueckfallen).
   */
  const kontoFuerSitzungKey = (sid: string) => `cui-lite-account-sid-${sid}`;

  // Resolve initial account: localStorage > prop > first account (user's switch must survive reload).
  // The cached value must be validated against THIS deployment's ACCOUNTS — a value cached while
  // pointed at a different deployment (e.g. a dev-only account id) is stale and must not silently
  // override a correct prop/server-assigned account (observed: 'gmail' cached for a partner-server
  // panel, where only sahori/kurt/auto/local exist, produced a permanent "unknown account" 400 on
  // every send until localStorage was cleared by hand).
  // Also validate the prop itself (not just the cache) — a stale layout config
  // (any of the several layout files per project/user/screen) can carry the same
  // kind of foreign account id, and the prop was previously trusted unconditionally.
  const validAccountId = accountId && ACCOUNTS.some(a => a.id === accountId) ? accountId : ACCOUNTS[0].id;
  const initialAccount = (() => {
    try {
      // Rafaels Wahl fuer GENAU diese Sitzung schlaegt alles — sie ueberlebt die Buehne.
      if (initialSessionId) {
        const fuerSitzung = localStorage.getItem(kontoFuerSitzungKey(initialSessionId));
        if (fuerSitzung && ACCOUNTS.some(a => a.id === fuerSitzung)) return fuerSitzung;
      }
      const cached = localStorage.getItem(storageKey);
      if (cached && ACCOUNTS.some(a => a.id === cached)) return cached;
      return validAccountId;
    } catch { return validAccountId; } // silent-ok: localStorage account read fails gracefully; defaults to prop value
  })();

  const [selectedId, setSelectedId] = useState(initialAccount);
  const [sessionId, setSessionId] = useState<string | null>(() => {
    if (initialSessionId) return initialSessionId;
    try { return localStorage.getItem(getSessionKey(initialAccount)); } catch { return null; }
  });
  const hadCachedMessagesRef = useRef(false);
  const [messages, setMessages] = useState<Message[]>(() => {
    if (initialSessionId || !sessionId) return [];
    try {
      const cached = localStorage.getItem(`cui-msgs-${sessionId}`);
      if (cached) { const parsed = JSON.parse(cached); if (parsed.length > 0) { hadCachedMessagesRef.current = true; return parsed; } }
    } catch {} // silent-ok: cached messages load failure; fresh poll renders messages
    return [];
  });
  const [isMountSyncing, setIsMountSyncing] = useState(() => hadCachedMessagesRef.current);
  const mountSyncDoneRef = useRef(false);
  /*
   * DER ANGEFANGENE SATZ UEBERLEBT DAS NEULADEN (Rafael, 07.09. 18:47Z).
   *
   * Vorher stand hier useState('') — der Entwurf lebte nur im React-State und war
   * nach jedem Rebuild-Reload weg, waehrend Konto, Sitzung und Nachrichten laengst
   * gesichert wurden. Ausgerechnet das Einzige, was nur er hat und niemand
   * wiederherstellen kann, war das Fluechtige.
   *
   * Der Schluessel haengt an der SITZUNG (siehe lib/entwuerfe): wer die Sitzung im
   * selben Kasten wechselt, findet seinen jeweiligen Satz wieder.
   */
  const entwurfKey = entwurfSchluessel(sessionId, panelId);
  const [input, setInput] = useState(() => leseEntwurf(entwurfSchluessel(sessionId, panelId)));

  // Sitzungswechsel im selben Kasten: den Entwurf der neuen Sitzung holen.
  const entwurfKeyRef = useRef(entwurfKey);
  useEffect(() => {
    if (entwurfKeyRef.current === entwurfKey) return;
    entwurfKeyRef.current = entwurfKey;
    setInput(leseEntwurf(entwurfKey));
  }, [entwurfKey]);

  // Jede Aenderung sichern; leer bedeutet loeschen (und gibt ein wartendes
  // Neuladen wieder frei).
  useEffect(() => { schreibeEntwurf(entwurfKey, input); }, [entwurfKey, input]);
  const [isLoading, setIsLoading] = useState(false);
  // Synchroner Send-Guard. isLoading reicht NICHT: zwei Enter-Keydowns aus dem
  // Tasten-Repeat (42ms Abstand, Vorfall 25.08.2026) laufen durch dieselbe
  // Closure, bevor React re-rendert — beide sehen isLoading=false und schicken
  // dieselbe Nachricht doppelt an /api/mission/send. Ein Ref greift sofort.
  const sendInFlightRef = useRef(false);
  // Nachrichten, die eintrafen, waehrend eine andere schon lief (Doppel-Enter,
  // zweiter Klick, oder einfach Tippen+Senden waehrend die Sitzung beschaeftigt
  // ist). Frueher wurden solche Sends still verworfen (nur console.warn) — genau
  // das darf laut Rafael (12.09.2026, "ueberall instant reagieren") nicht mehr
  // passieren: sichtbar reihen statt schlucken, automatisch zustellen sobald frei.
  const sendQueueRef = useRef<string[]>([]);
  const [pendingSendCount, setPendingSendCount] = useState(0);
  const [convStatus, setConvStatus] = useState<'ongoing' | 'completed'>('completed');
  const [showQueue, setShowQueue] = useState(() => {
    if (initialSessionId) return false;
    return !localStorage.getItem(getSessionKey(initialAccount));
  });
  const [queueRefresh, setQueueRefresh] = useState(0);
  const [attention, setAttention] = useState<'idle' | 'working' | 'needs_attention'>('idle');
  const [attentionReason, setAttentionReason] = useState<string | undefined>();

  // Propagate attention state to parent (LayoutManager) for project-tab coloring
  useEffect(() => {
    onStateChange?.(attention);
  }, [attention, onStateChange]);

  // Debounce isTabVisible to filter out transient flickers during FlexLayout
  // mount/restructure. node.isVisible() (LayoutManager.tsx:460) is evaluated per
  // render — a brief false→true wobble would otherwise tear down + rebuild the
  // entire effect chain (WebSocket, polling, panel-removed), producing the
  // "closed before connection established" reconnect storm in the console.
  // Threshold: 250ms — long enough to absorb FlexLayout re-render flicker,
  // short enough that real tab-switches still feel instant.
  const [isTabVisibleStable, setIsTabVisibleStable] = useState(isTabVisible);
  useEffect(() => {
    logCuiTelemetry({ ts: Date.now(), kind: 'isTabVisible-change', trigger: 'flexlayout:visibility-prop', panelId, raw: isTabVisible });
    if (isTabVisible) {
      setIsTabVisibleStable(true); // becoming visible: immediate (don't delay UI)
      return;
    }
    const t = setTimeout(() => {
      logCuiTelemetry({ ts: Date.now(), kind: 'isTabVisible-debounced-false', trigger: 'flexlayout:visibility-prop', panelId });
      setIsTabVisibleStable(false);
    }, 250);
    return () => clearTimeout(t);
  }, [isTabVisible, panelId]);

  const [deliveryStatus, setDeliveryStatus] = useState<Zustellstand | null>(null);
  useEffect(() => {
    setDeliveryStatus(null);
    if (!sessionId) return;
    const controller = new AbortController();
    const check = async () => {
      try {
        const response = await fetch(`/api/mission/conversation/${sessionId}/deliveries`, { signal: controller.signal });
        if (response.ok) {
          const data = await response.json();
          if (!controller.signal.aborted) setDeliveryStatus(data);
        }
      } catch { /* Existing connection status reports network errors. */ }
    };
    void check();
    const interval = setInterval(check, 10000);
    return () => { controller.abort(); clearInterval(interval); };
  }, [sessionId]);
  const [currentTool, setCurrentTool] = useState<{ toolName: string; toolDetail?: string; startedAt: number } | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null);
  /** Der Chat läuft, aber seine Bühne ist nicht entstanden (Server `eigene_buehne_fehler`, Befund 4d). */
  const [buehnenFehler, setBuehnenFehler] = useState<string | null>(null);
  const [convName, setConvName] = useState('');
  const [sessionModel, setSessionModel] = useState('');
  const [isPaused, setIsPaused] = useState(false);
  // Bühnenmodus-Ausnahme fuer GENAU diesen Chat (Kanzler, 19.09.2026, Rafael
  // 17.09.2026 ~13:0xZ: "es kann auch sein, dass ich alle Sessions per Buehne
  // mache und dann eine kurze Sitzung per Chat mache"). null = keine Ausnahme,
  // erbt den Regelfall (Haus/Person); 'an'/'aus' = dieser Chat weicht ab.
  const [manualFinished, setManualFinished] = useState(false);
  // Dim-when-working: grey out panels while Claude works so only "your turn" panels
  // stay bright. Layout never moves. Hover un-dims briefly; pin keeps bright permanently.
  const [isDimHovered, setIsDimHovered] = useState(false);
  const [dimPinned, setDimPinned] = useState(false);
  useEffect(() => {
    if (!sessionId) { setDimPinned(false); return; }
    try { setDimPinned(localStorage.getItem(`cui-dim-pin:${sessionId}`) === '1'); }
    catch { setDimPinned(false); }
  }, [sessionId]);
  const toggleDimPin = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setDimPinned(prev => {
      const next = !prev;
      try {
        if (sessionId) {
          if (next) localStorage.setItem(`cui-dim-pin:${sessionId}`, '1');
          else localStorage.removeItem(`cui-dim-pin:${sessionId}`);
        }
      } catch { /* localStorage unavailable */ }
      return next;
    });
  }, [sessionId]);
  const [reviewState, setReviewState] = useState<'idle' | 'running' | 'done'>('idle');
  const [planMode, setPlanMode] = useState(false);
  const [liveMode, setLiveMode] = useState(false);
  const [isAgentDone, _setIsAgentDone] = useState(false);
  const isAgentDoneRef = useRef(false);
  const setIsAgentDone = useCallback((v: boolean) => { isAgentDoneRef.current = v; _setIsAgentDone(v); }, []);
  const [showAllMessages, setShowAllMessages] = useState(false);
  const [expandTools, setExpandTools] = useState(() => {
    try { return localStorage.getItem('cui-expand-tools') === 'true'; } catch { return false; }
  });
  const toggleExpandTools = () => {
    const next = !expandTools;
    setExpandTools(next);
    try { localStorage.setItem('cui-expand-tools', String(next)); } catch {}
  };

  /*
   * MINIMALMODUS - die Vorgabe fuer JEDEN Chat (Rafael, 22.09.2026: "das
   * verbraucht einfach viel zu viele Buttons ... standardmaessig auf einen
   * Minimalmodus schalten und den Detailmodus nur per Klick").
   *
   * KEIN NEUER ZUSCHNITT. Was hier verschwindet, ist genau das, was `kompakt`
   * fuer die Guide-Spalte schon ausblendet (s. Prop-Doku oben): Konto-Auswahl,
   * Live/Refresh/Review/Dim/Pause/Finish, Modell- und LITE-Marke,
   * Plan/Loop/Stop/KILL, Kurzprompt-Kacheln, Diktat, Tool-Schalter. Neu ist
   * nur, WER es ausblendet - dort die Aufrufstelle, hier die Vorgabe.
   *
   * `kompakt` bleibt die HARTE Form: dort gibt es keinen Schalter, weil die
   * Spalte bewusst keine Werkstatt ist.
   *
   * EINE KOPFZEILE, ALLES WERKSTATTIGE HINTER ⋯ (Rafael, 03.10.2026: „der chat
   * hat viele funktionen die niemand braucht (oben gibts da dann wie rebuild,
   * cache und so weiter) – die leisten ganz oben sind chaotisch"). Bis dahin war
   * der Detailmodus ein Schalter, der die Werkstatt QUER durch das Panel
   * verteilte: eine zweite Kopfleiste (Konto, Static, Refresh, Review, Pin …),
   * eine Tools-Zeile, die Aktionszeile und die Kurzprompt-Kacheln ueber der
   * Eingabe — vier Leisten. Und weil die Wahl pro Panel in localStorage hielt,
   * blieb ein einmal geklickter Detailmodus fuer immer an (dasselbe Muster wie
   * bei den Buehnen-Reitern, cde07f2e7). Jetzt: Detail = das ⋯-Menue ist
   * aufgeklappt, als EIN Block direkt unter der Kopfzeile, und es haelt nicht
   * ueber das Panel hinaus. Der alte Schluessel `cui-chat-detail-v1:*` wird
   * nicht mehr gelesen.
   */
  const [menueOffen, setMenueOffen] = useState(false);
  const [serverPlanText, setServerPlanText] = useState<string | undefined>();
  const [sessionCwd, setSessionCwd] = useState<string | undefined>();
  // Persistent system messages (errors, warnings) — survive poll overwrites
  // MEMORY FIX: capped at 20 entries to prevent unbounded growth under error conditions.
  const pendingSystemMsgsRef = useRef<Message[]>([]);
  const pushPendingSystemMsg = (msg: Message) => {
    const arr = pendingSystemMsgsRef.current;
    arr.push(msg);
    if (arr.length > 20) pendingSystemMsgsRef.current = arr.slice(-20);
  };

  // --- Prompt Templates ---
  interface PromptTemplate { id: string; label: string; message: string; category: "reply" | "start"; subject?: string; order: number; createdAt: string; }
  const [replyTemplates, setReplyTemplates] = useState<PromptTemplate[]>([]);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [newTplLabel, setNewTplLabel] = useState("");
  const [newTplMessage, setNewTplMessage] = useState("");
  const [editingTemplate, setEditingTemplate] = useState<PromptTemplate | null>(null);

  // --- Auto-Inject (Loop Mode) ---
  const LOOP_PRESETS: { label: string; message: string }[] = [
    {
      label: "Test-Pyramide (Fix-Loop)",
      message: "Weiter mit der Test-Pyramide (bottom-up: Layer 0 vor 1 vor 2 vor 3 vor 4). Fuehre IMMER eine konkrete Aktion aus — NIEMALS nur 'Idle' oder 'Warte' antworten. Ablauf: (1) Pruefe ob ein Test gerade laeuft → monitore bis Ergebnis da ist. (2) Test FAIL? → Lies den Report, finde den echten Bug im APP-Code, fixe ihn, git commit + push, teste erneut. (3) Layer komplett PASS? → Starte naechsten Layer. (4) Alles gruen? → Melde Erfolg mit Score-Zusammenfassung. VERBOTEN: Szenario-Dateien oder Tester-Code aendern. NUR App-Code fixen.",
    },
    {
      label: "Nur testen (kein Fix)",
      message: "Fuehre alle Tests dieser App durch (Unified Tester, bottom-up: Layer 0 vor 1 vor 2 vor 3 vor 4). Berichte die Ergebnisse pro Layer mit Score-Tabelle. Aendere KEINEN Code — nur testen und dokumentieren.",
    },
    {
      label: "Weiter arbeiten",
      message: "Fuehre den naechsten anstehenden Task aus. Antworte NIEMALS nur mit 'Idle' oder 'Warte' — fuehre IMMER eine konkrete Aktion aus. Wenn kein Task definiert: pruefe failing Tests und arbeite sie ab.",
    },
  ];
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [loopIntervalMin, setLoopIntervalMin] = useState(5);
  const [loopMessage, setLoopMessage] = useState(LOOP_PRESETS[0].message);
  const [showLoopConfig, setShowLoopConfig] = useState(false);
  const [lastInjectTime, setLastInjectTime] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pasteUploading, setPasteUploading] = useState(false);
  const [fileDragOver, setFileDragOver] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [dropError, setDropError] = useState('');
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Feeds adaptive backoff in schedulePoll(). No hard-stop — loop self-heals.
  // Reset on success, on WS reconnect, and on relevant inbound events.
  const pollFailCountRef = useRef(0);
  const pollInFlightRef = useRef<string | null>(null);
  const panelWsRef = useRef<WebSocket | null>(null);
  const sessionIdRef = useRef<string | null>(sessionId);
  const selectedIdRef = useRef<string>(selectedId);
  // Track last poll data to skip redundant setState (avoids re-render + LCP shift)
  const lastPollHashRef = useRef('');
  const autoUnfinishedRef = useRef(false); // Track if we've already auto-unfinished for current session
  const manualFinishedRef = useRef(false); // Mirror for manualFinished state (accessible in callbacks)

  const account = ACCOUNTS.find(a => a.id === selectedId) || ACCOUNTS[0];
  // STATIC mode polls too — slowly. WS is the primary update path, but a browser
  // can hold a half-open socket (Safari energy saver, NAT timeout) that fires no
  // onclose: without a safety poll the chat silently freezes until the user sends
  // a message (Sahori, 2026-07-23 "Chat messages not appearing").
  const pollInterval = liveMode ? 3000 : 30000; // LIVE=3s, STATIC=30s safety poll

  // --- Polling (conversation only — states come via WebSocket) ---
  // Self-healing loop: failures only widen the next interval via pollFailCountRef.
  // Never hard-stops — recovers automatically when the server comes back, even if
  // no inbound WS event triggers a manual reset.
  const pollNow = useCallback(async () => {
    if (!sessionId) return;
    // Skip if server is known to be down (WS disconnected) — SessionStore will
    // re-arm via `cui-reconnected` event when it comes back, and ws.onopen here
    // also resets the fail count.
    // The first HTTP read must not wait for a stale WebSocket health flag.
    if (window.__cuiServerAlive === false && mountSyncDoneRef.current) return;
    const readKey = `${selectedId}:${sessionId}`;
    if (pollInFlightRef.current === readKey) return;
    pollInFlightRef.current = readKey;
    /* DIE SPUR ZUM ROTEN SATZ (KD 29.10.2026, Rafael 08:18:50Z: „Chatverlauf nicht geladen"): bis hierher stand ein
     * gescheiterter Verlaufs-Abruf nur in seiner Browser-Konsole — kein Status, keine Dauer auf dem Server. Jeder
     * Fehlschlag geht jetzt zusaetzlich in die vorhandene CUI-Telemetrie (cui-ws-telemetry.jsonl). */
    const pollStart = Date.now();
    const meldePollFehler = (status: number | null, fehler: string) => logCuiTelemetry({
      ts: Date.now(), kind: 'conv-poll-fehler', trigger: 'cui-lite-poll', panelId, sessionId, accountId: selectedId,
      status, fehler: fehler.slice(0, 300), dauerMs: Date.now() - pollStart, fehlversuch: pollFailCountRef.current,
      serverLebt: window.__cuiServerAlive ?? null,
    });
    try {
      const convResp = await fetch(`/api/mission/conversation/${selectedId}/${sessionId}?tail=50`, { signal: AbortSignal.timeout(15000) });
      if (sessionIdRef.current !== sessionId || selectedIdRef.current !== selectedId) return;
      if (convResp.ok) {
        const data = await convResp.json().catch(() => null);
        if (sessionIdRef.current !== sessionId || selectedIdRef.current !== selectedId) return; // silent-ok: invalid JSON response logged as warn below; poll retries
        if (!data || !Array.isArray(data.messages)) throw new Error('Der Server liefert keinen lesbaren Chat-Verlauf.');
        // --- Workspace invariant (fail-fast) --------------------------------
        // A panel must only ever display a session that belongs to ITS OWN
        // workspace. Authoritative home = the encoded dir the session's JSONL
        // physically lives in (server field `homeDir`). Compare it to this
        // panel's workDir encoded the same way ('/'→'-'). On a positive mismatch
        // we REFUSE the foreign session rather than render it: drop the stale
        // binding, clear cached messages, and fall back to this workspace's
        // queue. We reject only when BOTH sides are known, so an unresolved home
        // never blanks a legitimate panel (fail-fast on the error, safe on the
        // unknown). This makes cross-workspace session bleed structurally
        // impossible regardless of how a foreign binding got into the layout.
        const expectedHomeDir = workDir ? workDir.replace(/\//g, '-') : null;
        if (data.homeDir && expectedHomeDir && data.homeDir !== expectedHomeDir) {
          console.error(
            `[CuiLite] Workspace invariant violated: session ${sessionId} lives in `
            + `"${data.homeDir}" but this panel belongs to "${expectedHomeDir}". `
            + `Refusing to display a foreign session.`,
          );
          try { localStorage.removeItem(getSessionKey(selectedId)); } catch { /* silent-ok: localStorage may be disabled */ }
          try { localStorage.removeItem(`cui-msgs-${sessionId}`); } catch { /* silent-ok */ }
          setMessages([]);
          setSessionId(null);
          setShowQueue(true);
          onLoadFailed?.(sessionId);
          return;
        }
        if (typeof data.manualFinished === 'boolean') { manualFinishedRef.current = data.manualFinished; setManualFinished(data.manualFinished); }
        const newMsgs: Message[] = data.messages || [];
        const newStatus = data.status === 'ongoing' ? 'ongoing' : 'completed';
        const newPerms: Permission[] = (data.permissions || []).map((item: unknown, i: number) =>
          validateApiResponse<Permission>(item, `/api/mission/conversation/permissions[${i}]`, {
            id: 'string',
            type: 'string',
          })
        );
        const newName = data.customName || data.summary || '';
        const newDone = !!data.isAgentDone;
        // Fast hash: skip redundant setState when nothing changed
        // Include content-length of last 3 messages to detect streaming appends within existing blocks
        const lastTs = newMsgs[newMsgs.length - 1]?.timestamp || '';
        const tailLens = newMsgs.slice(-3).map(m => (m.content || '').length).join(',');
        const hash = `${newMsgs.length}|${lastTs}|${newStatus}|${newPerms.length}|${newName}|${newDone}|${data.backgroundPending ? 'bg' : ''}|${data.rateLimited || ''}|${tailLens}`;
        if (hash !== lastPollHashRef.current) {
          lastPollHashRef.current = hash;
          // Append any pending system messages (errors, warnings) that were added between polls
          const pending = pendingSystemMsgsRef.current;
          const merged = pending.length > 0 ? [...newMsgs, ...pending] : newMsgs;
          pendingSystemMsgsRef.current = []; // Clear after merge
          setMessages(trimMessages(merged));
          // Cache messages for instant load on next visit
          try { merkeNachrichtenCache(localStorage, sessionId, JSON.stringify(newMsgs.slice(-50))); } catch (err) { console.warn('[CuiLite] Nachrichten-Cache nicht geschrieben:', err); } // Cache, hoechstens MAX_NACHRICHTEN_CACHES Sitzungen (lib/browserSpeicher.ts)
          setConvStatus(newStatus as 'ongoing' | 'completed');
          setPermissions(newPerms);
          setConvName(newName);
          if (data.assignedModel) setSessionModel(data.assignedModel);
          if (typeof data.manualPaused === 'boolean') setIsPaused(data.manualPaused);
          setIsAgentDone(newDone);
          if (data.planText) setServerPlanText(data.planText);
          if (data.sessionCwd) setSessionCwd(data.sessionCwd);
          if (data.rateLimited) {
            setAttention('needs_attention');
            setAttentionReason('rate_limit');
          }
          // JSONL is source of truth: if agent is done or has pending permissions, override WS state
          if (newDone && !data.rateLimited) {
            // 'background' = turn ended, but a background task/agent/monitor the
            // assistant started is still running and will re-invoke it. Yellow,
            // not orange — orange means "paused, waiting for the user".
            const doneReason = data.backgroundPending ? 'background' : 'done';
            setAttention(prev => prev === 'needs_attention' ? prev : 'idle');
            setAttentionReason(prev => prev === 'rate_limit' ? prev : doneReason);
            setCurrentTool(null);
          } else if (newPerms.length > 0) {
            setAttention('needs_attention');
            setAttentionReason('permission');
          }
        }
        pollFailCountRef.current = 0;
        onLoadSucceeded?.();
        if (!mountSyncDoneRef.current) { mountSyncDoneRef.current = true; setIsMountSyncing(false); }
      } else {
        // HTTP errors (502, 503, 5xx during deploy etc.) — count as failures.
        // schedulePoll() widens the next interval; we keep retrying forever until
        // either the server returns 2xx or window.__cuiServerAlive flips to false.
        pollFailCountRef.current++;
        // Der Server sagt bei 404/502 woertlich, in welchem Haus gesucht wurde und wo der
        // Verlauf liegt (mission.ts, verlaufAusDemAnderenHaus) — das zeigen statt der nackten Zahl.
        const fehler = await convResp.json().then((d: unknown) => (d && typeof (d as { error?: unknown }).error === 'string' ? (d as { error: string }).error : ''), () => '');
        meldePollFehler(convResp.status, fehler);
        if (sessionIdRef.current !== sessionId || selectedIdRef.current !== selectedId) return;
        onLoadError?.(`Chat-Verlauf nicht geladen (HTTP ${convResp.status}${fehler ? `: ${fehler}` : ''}). Die Verbindung wird weiter geprüft.`);
        if (pollFailCountRef.current === 1 || pollFailCountRef.current === 5) {
          console.warn(`[CuiLite] Poll ${convResp.status} for ${selectedId} (fail #${pollFailCountRef.current})`);
        }
      }
    } catch (err) {
      meldePollFehler(null, err instanceof Error ? `${err.name}: ${err.message}` : String(err));
      if (sessionIdRef.current !== sessionId || selectedIdRef.current !== selectedId) return;
      pollFailCountRef.current++;
      // fail loud: silent catches were what masked the circuit-breaker bug for weeks
      console.error(`[CuiLite] Poll exception for ${selectedId} (fail #${pollFailCountRef.current}):`, err);
      onLoadError?.(`Chat-Verlauf nicht geladen: ${err instanceof Error ? err.message : String(err)}. Die Verbindung wird weiter geprüft.`);
    } finally {
      if (pollInFlightRef.current === readKey) pollInFlightRef.current = null;
    }
  }, [sessionId, selectedId, workDir, panelId, onLoadSucceeded, onLoadError]);

  // Fetch states once on mount/account change (WS handles updates after that)
  useEffect(() => {
    if (window.__cuiServerAlive !== true) return;
    fetch('/api/mission/states', { signal: AbortSignal.timeout(10000) })
      .then(r => r.ok ? r.json() : null)
      .then((states: Record<string, AgentSessionState> | null) => {
        if (!states) return;
        // States are keyed by sessionId, not accountId — look up by current session or find by account
        const sid = sessionIdRef.current;
        const myState: AgentSessionState | undefined = sid ? states[sid]
          : Object.values(states).find((s) => s.accountId === selectedId && s.state === 'working');
        if (myState) {
          setAttention(myState.state || 'idle');
          setAttentionReason(myState.reason);
        }
      })
      .catch(() => { /* timeout expected on slow connections — WS will sync state */ });
  }, [selectedId]);

  // Adaptive polling with recursive setTimeout (interval adjusts to failure count)
  useEffect(() => {
    if (!sessionId || !isTabVisibleStable) {
      if (!isTabVisibleStable) { /* paused: tab not visible */ }
      return;
    }
    let cancelled = false;
    // Exponential backoff with cap: never hard-stops. After a server-side blip
    // (deploy, restart, transient 5xx), polling slows down (max 30s) but keeps
    // checking, so the panel self-heals without needing an inbound WS event to
    // re-arm. Runs in BOTH modes: LIVE at 3s, STATIC at 30s (safety net against
    // silently-dead WS connections; WS remains the primary update path).
    const BACKOFF_CAP_MS = 30000;
    const schedulePoll = () => {
      if (cancelled) return;
      const fails = pollFailCountRef.current;
      // LIVE: 0 fails → 3s; 1 → 6s; 2 → 12s; 3 → 24s; 4+ → 30s cap. STATIC: always 30s.
      const delay = !mountSyncDoneRef.current
        ? Math.min(1000 * Math.pow(2, Math.max(0, fails - 1)), 5000)
        : Math.min(pollInterval * Math.pow(2, fails), BACKOFF_CAP_MS);
      pollTimerRef.current = setTimeout(async () => {
        if (cancelled) return;
        await pollNow();
        schedulePoll();
      }, delay);
    };
    // Initial poll then start schedule
    pollNow().then(() => { if (!cancelled) schedulePoll(); }).catch((err) => { console.error('[CuiLite] Initial poll error:', err); });
    return () => {
      cancelled = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [sessionId, selectedId, isTabVisibleStable, pollNow, pollInterval, refreshToken]);

  // --- Fetch Prompt Templates (retry on failure — server may be restarting) ---
  const loadTemplates = useCallback(() => {
    fetch('/api/prompt-templates', { signal: AbortSignal.timeout(10000) })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        const reply = (data.templates || []).filter((t: PromptTemplate) => t.category === 'reply');
        reply.sort((a: PromptTemplate, b: PromptTemplate) => a.order - b.order);
        setReplyTemplates(reply);
      })
      .catch(() => { /* timeout expected — templates load lazily on next attempt */ });
  }, []);
  useEffect(() => {
    if (window.__cuiServerAlive !== true) return;
    loadTemplates();
  }, [loadTemplates]);

  // Keep sessionIdRef in sync (avoids WS reconnection on every session change)
  useEffect(() => {
    sessionIdRef.current = sessionId;
    selectedIdRef.current = selectedId;
    autoUnfinishedRef.current = false; // Reset on session change so new session can be auto-unfinished
    manualFinishedRef.current = false;
    setManualFinished(false);
    // Report visibility change to server (session exclusivity)
    const ws = panelWsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'panel-visibility', panelId, projectId, accountId: selectedId, sessionId: sessionId || '', route: sessionId ? `/c/${sessionId}` : '' }));
    }
  }, [sessionId, selectedId, panelId, projectId]);

  // --- Notify server when tab becomes hidden (clear visibility immediately) ---
  // Uses STABLE flag — raw isTabVisible flickers during FlexLayout re-mount,
  // which would otherwise produce spurious panel-removed POSTs (server then
  // thinks the panel is gone and broadcasts cascading invalidations).
  useEffect(() => {
    if (!isTabVisibleStable) {
      logCuiTelemetry({
        ts: Date.now(), kind: 'panel-removed-POST', trigger: 'flexlayout:visibility-prop', panelId, projectId, reason: 'isTabVisibleStable=false',
      });
      fetch(`/api/mission/panel-removed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ panelId, projectId }),
      }).catch(() => {}); // silent-ok: panel-removed notification is best-effort
    }
  }, [isTabVisibleStable, panelId, projectId]);

  // --- WS for realtime attention events (auto-reconnect) ---
  useEffect(() => {
    if (!isTabVisibleStable) return;
    let disposed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoff = 1000; // start at 1s, doubles up to 30s max
    let connectAttempts = 0; // telemetry: count attempts in this effect-instance
    const effectInstanceId = Math.random().toString(36).slice(2, 8);

    // Telemetry: events flow into window.__cuiTelemetry (in-memory ringbuffer for
    // DevTools inspection) AND POST to /api/mission/telemetry/cui-ws so the
    // assistant can read the diagnostic log from disk without asking the user.
    const log = (kind: string, extra: Record<string, unknown> = {}) => {
      logCuiTelemetry({
        ts: Date.now(), kind, trigger: 'panel-ws-lifecycle', panelId, sessionId: sessionIdRef.current?.slice(0, 8),
        accountId: selectedId, effectInstanceId, ...extra,
      });
    };
    log('effect-run');

    const connect = () => {
      if (disposed) return;
      // Don't hammer WS when server is down — wait for App WS to restore __cuiServerAlive
      if (window.__cuiServerAlive === false) {
        log('connect-deferred-server-dead');
        reconnectTimer = setTimeout(connect, Math.min(backoff, 10000));
        return;
      }
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      // Nebenkanal, kein Fenster: nicht Ziel der Fensterabfrage (server/lib/guide-fenster-abfrage.ts).
      const ws = new WebSocket(`${protocol}://${window.location.host}/ws?kanal=neben`);
      const connectStartedAt = Date.now();
      connectAttempts += 1;
      log('ws-connect-start', { attempt: connectAttempts });
      panelWsRef.current = ws;
      ws.onerror = () => { log('ws-error', { dur: Date.now() - connectStartedAt, readyState: ws.readyState }); };

      ws.onopen = () => {
        const dur = Date.now() - connectStartedAt;
        log('ws-open', { dur });
        backoff = 1000; // reset backoff on successful connection
        console.log(`[CuiLite WS] Connected (panel=${(panelId?.slice(0,8) ?? '<no-panel>')} dur=${dur}ms attempt=${connectAttempts})`);
        // Server is back — clear backoff and poll immediately
        pollFailCountRef.current = 0;
        const sid = sessionIdRef.current;
        if (sid) setTimeout(pollNow, 300);
        // Re-fetch templates (may have failed during server restart)
        loadTemplates();
        // Report panel visibility to server (session exclusivity)
        ws.send(JSON.stringify({ type: 'panel-visibility', panelId, projectId, accountId: selectedId, sessionId: sid || '', route: sid ? `/c/${sid}` : '' }));
        // Re-sync state from server (handles server restarts where in-memory states are lost)
        if (sid) {
          fetch('/api/mission/states').then(r => r.ok ? r.json() : null).then(states => {
            if (!states || disposed) return;
            const serverState = states[sid];
            if (!serverState) {
              // Server doesn't know this session — stale state from before restart
              console.log(`[CuiLite WS] Session ${sid.slice(0, 8)} not in server states — resetting to idle`);
              setAttention('idle');
              setAttentionReason('done');
            } else if (serverState.state === 'working') {
              setAttention('working');
            } else if (serverState.state === 'needs_attention') {
              setAttention('needs_attention');
              setAttentionReason(serverState.reason);
            } else if (serverState.state === 'idle') {
              setAttention(prev => prev === 'needs_attention' ? prev : 'idle');
              setAttentionReason(serverState.reason || 'done');
            }
          }).catch(() => {}); // silent-ok: state re-sync on WS reconnect is non-critical; events will update state
        }
      };
      ws.onclose = (ev) => {
        log('ws-close', { dur: Date.now() - connectStartedAt, code: ev.code, reason: ev.reason, readyStateBefore: ws.readyState, disposed });
        if (!disposed) {
          if (backoff <= 1000) console.log(`[CuiLite WS] Disconnected, reconnecting... (panel=${(panelId?.slice(0,8) ?? '<no-panel>')} code=${ev.code})`);
          reconnectTimer = setTimeout(() => {
            backoff = Math.min(backoff * 2, 30000);
            connect();
          }, backoff);
        }
      };

      ws.onmessage = (e) => {
        try {
          const raw = e.data as string;
          // Session exclusivity: check BEFORE accountId filter (message contains claiming panel's account, not ours)
          const currentSid = sessionIdRef.current;
          if (raw.includes('session-claimed') && currentSid) {
            const claimMsg = JSON.parse(raw);
            if (claimMsg.type === 'session-claimed' && claimMsg.evictPanelId === panelId && claimMsg.sessionId === currentSid) {
              log('session-claimed-evict', {
                claimedBy: claimMsg.claimedByPanelId,
                evictPanel: panelId,
                evictSession: currentSid.slice(0, 8),
              });
              console.log(`[CuiLite] Session ${currentSid.slice(0, 8)} claimed by ${claimMsg.claimedByPanelId} — closing chat`);
              setSessionId(null);
              setShowQueue(true);
              setMessages([]);
              setPermissions([]);
              setConvName('');
              setSessionModel('');
              setAttention('idle');
              setAttentionReason(undefined);
              setRateLimitMessage(null);
              setLiveMode(false);
              if (persistSession) try { localStorage.removeItem(getSessionKey(selectedId)); } catch {} // silent-ok: localStorage may be disabled
              onRouteChange?.('');
              return;
            }
          }
          // Navigate to conversation: handle before accountId filter (message may not contain our accountId)
          if (raw.includes('cui-navigate-conversation') && panelId) {
            const navMsg = JSON.parse(raw);
            if (navMsg.type === 'control:cui-navigate-conversation' && navMsg.panelId === panelId) {
              console.log(`[CuiLite] Navigate to session ${navMsg.sessionId?.slice(0, 8)} (panel=${panelId})`);
              setSessionId(navMsg.sessionId);
              setShowQueue(false);
              pollFailCountRef.current = 0;
              setTimeout(pollNow, 300);
              return;
            }
          }
          if (raw.includes('conv-account-status')) {
            const status = JSON.parse(raw);
            if (status.type === 'conv-account-status' && status.sessionId === sessionIdRef.current) {
              const label = status.label || accountAccessLabel(status.effectiveAccountId);
              const text = status.status === 'active' ? `Aktiver KI-Zugang: ${label}`
                : status.status === 'pending' ? `Kontowechsel vorgemerkt: ${label} — nach Zugende.`
                : status.status === 'ready' ? `Nächster Auftrag: ${label}. Aktuell kein laufender Prozess.`
                : `Kontowechsel nicht ausgeführt: ${status.reason || 'Status prüfen'}`;
              setMessages(prev => trimMessages([...prev, { role: 'system', content: text, timestamp: new Date().toISOString() }]));
              return;
            }
          }
          // Handle account change from another panel (e.g. assign endpoint). Same
          // validation as the initial-mount resolution above — this broadcast was
          // trusted unconditionally and is a second way a foreign account id
          // (e.g. a dev-only id on the partner server) could poison selectedId.
          if (raw.includes("conv-account-changed")) {
            const acMsg = JSON.parse(raw);
            if (acMsg.type === "conv-account-changed" && acMsg.sessionId === sessionIdRef.current && acMsg.accountId !== selectedIdRef.current) {
              if (!ACCOUNTS.some(a => a.id === acMsg.accountId)) {
                console.warn(`[CuiLite] Ignoring conv-account-changed to invalid account '${acMsg.accountId}' (not in this deployment's ACCOUNTS)`);
                return;
              }
              console.log(`[CuiLite] Account changed externally: ${selectedIdRef.current} -> ${acMsg.accountId}`);
              setSelectedId(acMsg.accountId);
              try { localStorage.setItem(storageKey, acMsg.accountId); } catch {} // silent-ok: localStorage may be disabled
              try { localStorage.setItem(kontoFuerSitzungKey(acMsg.sessionId), acMsg.accountId); } catch {} // silent-ok: localStorage may be disabled
              setMessages(prev => trimMessages([...prev, { role: "system", content: `KI-Zuordnung aktualisiert → ${accountAccessLabel(acMsg.accountId)}`, timestamp: new Date().toISOString() }]));
              return;
            }
          }
          // Allow messages for selected account OR for the current session (cross-account visibility)
          const currentSid2 = sessionIdRef.current;
          if (!raw.includes(selectedId) && !(currentSid2 && raw.includes(currentSid2))) return;
          const msg = JSON.parse(raw);
          if (msg.type === 'conv-review-started' && msg.sessionId === sessionIdRef.current) {
            setReviewState('running');
          } else if (msg.type === 'conv-review-complete' && msg.sessionId === sessionIdRef.current) {
            setReviewState('done');
            setTimeout(() => setReviewState('idle'), 8000);
          } else if (msg.type === 'conv-paused' && msg.sessionId === sessionIdRef.current) {
            setIsPaused(!!msg.paused);
          } else if (msg.type === 'conv-model-changed' && msg.sessionId === sessionIdRef.current) {
            setSessionModel(msg.model || '');
          } else if (msg.type === 'conv-attention' && (msg.accountId === selectedId || msg.key === selectedId || msg.sessionId === currentSid2)) {
            // JSONL poll (isAgentDone) is source of truth — don't let stale WS "working" override it
            if (msg.state === 'working' && isAgentDoneRef.current) {
              // Server thinks working, but JSONL says done — ignore, poll will correct
            } else {
              setAttention(msg.state);
              setAttentionReason(msg.reason);
            }
            if (msg.state === 'needs_attention' && sessionIdRef.current) {
              pollFailCountRef.current = 0;
              setTimeout(pollNow, 500);
            }
            // If WS says working and agent was done, it might actually be working again — re-poll to verify
            if (msg.state === 'working' && isAgentDoneRef.current) {
              setTimeout(pollNow, 1000);
            }
          }
          if (msg.type === 'cui-state' && (msg.cuiId === selectedId || msg.sessionId === currentSid2)) {
            pollFailCountRef.current = 0;
            if (msg.state === 'processing') {
              // Only trust "processing" if JSONL doesn't say done, OR trigger poll to verify
              if (!isAgentDoneRef.current) {
                setAttention('working');
                setAttentionReason(undefined);
                setRateLimitMessage(null);
              } else {
                // Agent was done but WS says processing — might be restarted, re-poll
                setTimeout(pollNow, 500);
              }
            }
            if (msg.state === 'done') {
              // Don't overwrite rate_limit state — user needs to see it
              setAttention(prev => prev === 'needs_attention' ? prev : 'idle');
              setAttentionReason(prev => prev === 'rate_limit' ? prev : (msg.backgroundPending ? 'background' : 'done'));
              setCurrentTool(null);
              if (liveMode) setLiveMode(false); // Auto-exit LIVE when done
              if (sessionIdRef.current) {
                setTimeout(pollNow, 500);
              }
            }
            if (msg.state === "error" && msg.message) {
              setAttention("needs_attention");
              const isRateLimit = msg.message.toLowerCase().includes('rate limit') || msg.message.toLowerCase().includes('nutzungslimit');
              setAttentionReason(isRateLimit ? "rate_limit" : "error");
              setRateLimitMessage(msg.message);
              if (sessionIdRef.current) setTimeout(pollNow, 1000);
            }
          }
          // Tool execution tracking
          if (msg.type === 'tool-executing' && msg.sessionId === currentSid2) {
            setCurrentTool({ toolName: msg.toolName, toolDetail: msg.toolDetail, startedAt: msg.startedAt });
          }
          if (msg.type === 'tool-done' && msg.sessionId === currentSid2) {
            setCurrentTool(null);
          }
          if (msg.type === 'tool-heartbeat' && msg.sessionId === currentSid2) {
            setCurrentTool(prev => prev || { toolName: msg.toolName, toolDetail: msg.toolDetail, startedAt: Date.now() - (msg.elapsedMs || 0) });
          }
          // Live character streaming (LIVE mode only)
          if (msg.type === 'cli-partial' && msg.sessionId === sessionIdRef.current && liveMode) {
            setMessages(prev => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant' && last._streaming) {
                return [...prev.slice(0, -1), { ...last, content: msg.content }];
              }
              return [...prev, { role: 'assistant' as const, content: msg.content, _streaming: true, timestamp: new Date().toISOString() }];
            });
          }
          // Per-turn streaming: assistant message arrived via WS (real-time, no polling needed)
          if (msg.type === 'turn-update' && msg.sessionId === sessionIdRef.current) {
            setMessages(prev => {
              const last = prev[prev.length - 1];
              // Replace streaming partial with final turn
              if (last?._streaming) {
                return [...prev.slice(0, -1), msg.message];
              }
              // Dedup: skip if last message has identical content (poll may have already added it)
              if (last?.role === 'assistant') {
                const lastContent = typeof last.content === 'string' ? last.content : JSON.stringify(last.content);
                const newContent = typeof msg.message.content === 'string' ? msg.message.content : JSON.stringify(msg.message.content);
                if (lastContent === newContent) return prev;
              }
              return [...prev, msg.message];
            });
            lastPollHashRef.current = ''; // Invalidate so next poll re-applies ground truth
          }
          // Queue refresh on state changes
          if ((msg.type === 'cui-state' || msg.type === 'cui-response-ready' || msg.type === 'turn-update') && (msg.cuiId === selectedId || msg.accountId === selectedId || msg.sessionId === currentSid2)) {
            setQueueRefresh(n => n + 1);
          }
        } catch (err) {
          console.warn('[CuiLite] WS parse error:', (err as Error).message);
        }
      };
    };

    connect();

    // Listen for SessionStore reconnection — immediately reconnect this panel's WS
    const onServerReconnected = () => {
      if (disposed) return;
      console.log('[CuiLite WS] Server reconnected via SessionStore, immediate reconnect');
      if (reconnectTimer) clearTimeout(reconnectTimer);
      backoff = 1000;
      pollFailCountRef.current = 0;
      const existing = panelWsRef.current;
      if (existing) { existing.onclose = null; existing.close(); panelWsRef.current = null; }
      connect();
    };
    window.addEventListener('cui-reconnected', onServerReconnected);

    return () => {
      disposed = true;
      log('effect-cleanup', { reconnectAttempts: connectAttempts });
      window.removeEventListener('cui-reconnected', onServerReconnected);
      // CRITICAL: close the WebSocket to prevent connection leak.
      // BUT: calling .close() while the socket is still CONNECTING produces a
      // "WebSocket is closed before the connection is established" console warning
      // AND aborts the handshake; if the cleanup is caused by a transient render
      // flicker (e.g. node.isVisible() momentarily false during FlexLayout
      // re-mount), the next effect-run kicks off another connect → storm.
      // Solution: if still CONNECTING, defer the close to onopen so the handshake
      // completes cleanly first.
      const ws = panelWsRef.current;
      if (ws) {
        ws.onclose = null;
        ws.onerror = null;
        if (ws.readyState === WebSocket.CONNECTING) {
          log('ws-deferred-close-while-connecting');
          ws.onopen = () => { try { ws.close(); } catch { /* ignore */ } };
        } else {
          log('ws-close-on-cleanup', { readyState: ws.readyState });
          try { ws.close(); } catch { /* ignore */ }
        }
      }
      panelWsRef.current = null;
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [selectedId, isTabVisibleStable]); // sessionId via ref (no reconnection on navigate), pollNow/loadTemplates are stable callbacks

  // Auto-scroll only when user is near bottom (not scrolled up reading)
  useEffect(() => {
    if (!userScrolledUpRef.current) {
      scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' });
      setUnreadCount(0);
    } else {
      setUnreadCount(c => c + 1);
    }
  }, [messages]);

  // Listen for "All Live" broadcast from workspace toolbar
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.live !== undefined) { setLiveMode(detail.live); }
    };
    window.addEventListener('cui-all-live', handler);
    return () => window.removeEventListener('cui-all-live', handler);
  }, []);

  // --- Handlers ---

  // --- Auto-Inject (Loop) Sync ---
  const syncLoopState = useCallback(async () => {
    if (window.__cuiServerAlive !== true || !sessionId || !betreiberWege) return;
    try {
      const r = await fetch(`/api/auto-inject/session/${sessionId}`, { signal: AbortSignal.timeout(10000) });
      if (!r.ok) return;
      const data = await r.json().catch(() => null); // silent-ok: auto-inject config response parse failure; feature disabled until next poll
      if (!data) return;
      if (data.config) {
        setLoopEnabled(data.config.enabled);
        setLoopIntervalMin(Math.round(data.config.intervalMs / 60000));
        setLoopMessage(data.config.message);
      } else {
        setLoopEnabled(false);
      }
      setLastInjectTime(data.lastInject || null);
    } catch { /* timeout expected on slow connections */ }
  }, [sessionId, betreiberWege]);

  useEffect(() => { syncLoopState(); }, [syncLoopState]);

  const toggleLoop = useCallback(async (enable: boolean) => {
    if (!selectedId || !sessionId) return;
    if (window.__cuiServerAlive === false) {
      console.warn('[CuiLite] Toggle loop blocked: server not alive');
      return;
    }
    try {
      if (enable) {
        await fetch("/api/auto-inject", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: AbortSignal.timeout(20000),
          body: JSON.stringify({
            accountId: selectedId,
            sessionId,
            workDir,
            message: loopMessage,
            intervalMs: loopIntervalMin * 60000,
            enabled: true,
          }),
        });
      } else {
        await fetch("/api/auto-inject", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: AbortSignal.timeout(20000),
          body: JSON.stringify({ accountId: selectedId, sessionId, enabled: false }),
        });
      }
      setLoopEnabled(enable);
      syncLoopState();
    } catch (err) { console.warn('[CuiLite] Loop toggle error:', (err as Error).message); }
  }, [selectedId, sessionId, workDir, loopMessage, loopIntervalMin, syncLoopState]);

  const saveLoopConfig = useCallback(async () => {
    if (!selectedId) return;
    if (window.__cuiServerAlive === false) {
      console.warn('[CuiLite] Save loop config blocked: server not alive');
      return;
    }
    try {
      await fetch("/api/auto-inject", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: AbortSignal.timeout(20000),
        body: JSON.stringify({
          accountId: selectedId,
          sessionId,
          workDir,
          message: loopMessage,
          intervalMs: loopIntervalMin * 60000,
          enabled: loopEnabled,
        }),
      });
      syncLoopState();
    } catch (err) { console.warn('[CuiLite] Save loop config error:', (err as Error).message); }
    setShowLoopConfig(false);
  }, [selectedId, sessionId, workDir, loopMessage, loopIntervalMin, loopEnabled, syncLoopState]);

  const handleSaveTemplate = useCallback(async () => {
    if (!newTplLabel.trim() || !newTplMessage.trim()) return;
    if (window.__cuiServerAlive === false) {
      console.warn('[CuiLite] Save template blocked: server not alive');
      return;
    }
    try {
      if (editingTemplate) {
        const resp = await fetch(`/api/prompt-templates/${editingTemplate.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label: newTplLabel, message: newTplMessage }),
          signal: AbortSignal.timeout(20000),
        });
        if (resp.ok) {
          const data = await resp.json().catch(() => null); // silent-ok: template response parse failure; outer catch logs the error
          if (data?.template) setReplyTemplates(prev => prev.map(t => t.id === data.template.id ? data.template : t));
        }
      } else {
        const resp = await fetch('/api/prompt-templates', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label: newTplLabel, message: newTplMessage, category: 'reply' }),
          signal: AbortSignal.timeout(20000),
        });
        if (resp.ok) {
          const data = await resp.json().catch(() => null); // silent-ok: template response parse failure; outer catch logs the error
          if (data?.template) setReplyTemplates(prev => [...prev, data.template]);
        }
      }
    } catch (err) { console.warn('[CuiLite] Save template error:', (err as Error).message); }
    setShowTemplateForm(false);
    setEditingTemplate(null);
    setNewTplLabel('');
    setNewTplMessage('');
  }, [newTplLabel, newTplMessage, editingTemplate]);

  const handleDeleteTemplate = useCallback(async (id: string) => {
    if (window.__cuiServerAlive === false) {
      console.warn('[CuiLite] Delete template blocked: server not alive');
      return;
    }
    try {
      const resp = await fetch(`/api/prompt-templates/${id}`, { method: 'DELETE', signal: AbortSignal.timeout(20000) });
      if (resp.ok) setReplyTemplates(prev => prev.filter(t => t.id !== id));
    } catch (err) { console.warn('[CuiLite] Delete template error:', (err as Error).message); }
  }, []);

  // Drag&drop of arbitrary files (PDF/Excel/docs) directly onto the chat.
  // Each file is uploaded, then ONE combined readCommand is injected into the input —
  // so multiple documents land in the chat in one go instead of copying paths one by one.
  // Core upload path — shared by drag&drop AND the 📎 attach button (file picker).
  // Partners kept looking for a "Heftklammer" that didn't exist; only drop/paste worked.
  const uploadFileList = useCallback(async (fileArr: File[]) => {
    if (!fileArr || fileArr.length === 0) return;
    if (window.__cuiServerAlive === false) return;

    setFileUploading(true);
    setDropError('');
    const paths: string[] = [];
    const failed: string[] = [];
    for (const file of fileArr) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch('/api/uploads/file', {
          method: 'POST',
          body: formData,
          signal: AbortSignal.timeout(120000),
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
          throw new Error(errBody.error || `HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!data?.path) throw new Error('Server lieferte keinen Pfad');
        paths.push(data.path as string);
        if (data.warnung) failed.push(data.warnung as string);
      } catch (err: any) {
        failed.push(`${file.name}: ${err.message}`);
      }
    }
    if (paths.length > 0) {
      const readCommand = paths.length === 1
        ? `Schau dir diese Datei an: ${paths[0]}`
        : `Schau dir diese ${paths.length} Dateien an:\n${paths.map(p => `- ${p}`).join('\n')}`;
      setInput(prev => (prev ? prev + '\n' : '') + readCommand);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
    if (failed.length > 0) {
      // Fail loud: surface every file that did not make it, don't swallow.
      setDropError(`${failed.length} fehlgeschlagen — ${failed.join('  |  ')}`);
    }
    setFileUploading(false);
  }, []);

  // Drag&drop wrapper — delegates to the shared upload core.
  const handleFileDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setFileDragOver(false);
    const dropped = e.dataTransfer?.files;
    if (!dropped || dropped.length === 0) return;
    await uploadFileList(Array.from(dropped));
  }, [uploadFileList]);

  // 📎 attach button → open OS file picker, upload selection via the shared core.
  const handleAttachClick = useCallback(() => fileInputRef.current?.click(), []);
  const handleFilePicked = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files;
    if (picked && picked.length > 0) await uploadFileList(Array.from(picked));
    e.target.value = ''; // allow re-picking the same file
  }, [uploadFileList]);

  // Unfinish a manually-finished session (called on explicit user interaction only)
  const doUnfinish = useCallback((sid: string) => {
    if (!manualFinishedRef.current) return;
    manualFinishedRef.current = false;
    setManualFinished(false);
    fetch(`/api/mission/conversation/${sid}/finish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-cui-eingabe': 'mensch' },
      body: JSON.stringify({ finished: false }),
      signal: AbortSignal.timeout(5000),
    }).catch(() => {}); // silent-ok: unfinish API call is best-effort; WS turn event will restore working state
  }, []);

  /**
   * Ist eine Nachricht tatsaechlich beim Server angekommen?
   *
   * Wird gebraucht, wenn das Senden ohne Antwort abbricht. "Keine Antwort" ist
   * NICHT "nicht angekommen": /api/mission/send muss bei totem CLI-Prozess erst
   * einen neuen starten und die Sitzung fortsetzen — das dauert und laeuft in
   * den 65s-Timeout, waehrend der Server die Nachricht laengst angenommen hat.
   * Genau daran ist es am 16.08.2026 gescheitert (Server-Log belegt den Eingang,
   * der Client hielt es fuer fehlgeschlagen).
   *
   * Statt zu raten wird nachgesehen: der Verlauf liegt auf dem Server.
   * true = nachweislich angekommen · false = nachweislich nicht angekommen ·
   * null = die Pruefung selbst war nicht moeglich (dann NICHT so tun, als wuesste
   * man es).
   */
  const confirmDelivery = useCallback(async (sid: string, sentText: string): Promise<boolean | null> => {
    const needle = sentText.trim();
    if (!needle) return null;
    // Die Zeile taucht erst auf, wenn der Server sie ins Transkript geschrieben
    // hat — ein paar Sekunden Nachlauf sind normal, deshalb wenige kurze Versuche.
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) await new Promise(r => setTimeout(r, 2000));
      try {
        const r = await fetch(`/api/mission/conversation/${selectedId}/${sid}?tail=50`, {
          signal: AbortSignal.timeout(15000),
        });
        if (!r.ok) continue;
        const data = await r.json().catch(() => null);
        if (!data || !Array.isArray(data.messages)) continue;
        const hit = (data.messages as Message[]).some(m =>
          m.role === 'user' && typeof m.content === 'string'
          && (m.content.trim() === needle || m.content.includes(needle)),
        );
        if (hit) return true;
      } catch (err) {
        console.warn('[CuiLite] Zustellpruefung fehlgeschlagen:', err);
        return null; // Server nicht erreichbar — wir wissen es schlicht nicht.
      }
    }
    return false; // Server geantwortet, Nachricht steht nicht im Verlauf.
  }, [selectedId]);

  // Fuehrt EINE Nachricht tatsaechlich ueber /api/mission/send zu. Wird von
  // handleSend fuer den ersten (freien) Send aufgerufen UND von sich selbst,
  // um die Warteschlange (sendQueueRef) nach Abschluss leerzuraeumen — die
  // Nachricht ist an dieser Stelle bereits im Transkript sichtbar und der
  // Eingabefeld-Text bereits geleert; overrideMessage-Sends (Templates,
  // Loop-Inject) haengen NICHT am Eingabefeld.
  const runSend = useCallback(async (msg: string): Promise<void> => {
    if (!sessionId) {
      // Aufrufer (handleSend/handleRespond) pruefen dies bereits vor dem Aufruf —
      // das hier ist nur das Sicherheitsnetz, falls runSend je ohne diese Pruefung
      // aufgerufen wird. Nicht wortlos verschwinden lassen.
      console.error('[CuiLite] runSend ohne sessionId aufgerufen — Nachricht NICHT gesendet:', msg);
      const note: Message = { role: 'system', content: `Interner Fehler: keine Sitzung gebunden — Nachricht NICHT gesendet. Dein Text:\n\n${msg}`, timestamp: new Date().toISOString() };
      pushPendingSystemMsg(note);
      setMessages(prev => trimMessages([...prev, note]));
      return;
    }
    // Acquire als erste Anweisung — das finally garantiert die Freigabe auf
    // JEDEM Pfad (Erfolg, HTTP-Fehler, Timeout, Exception). Ein haengender
    // Guard wuerde dieses Panel sonst dauerhaft stumm schalten.
    sendInFlightRef.current = true;
    setIsLoading(true);
    try {
      let resp: Response;
      for (let attempt = 0; ; attempt++) {
        const response = await fetch('/api/mission/send', {
        method: 'POST',
        // MENSCH-MARKER: dieses Feld setzt nur das Chat-Fenster der CUI. Agenten,
        // die einander ueber dieselbe Route schreiben, setzen es nicht — daran, und
        // nur daran, erkennt der Guide Rafaels eigene Antwort. Siehe `von` in
        // server/routes/mission.ts.
        headers: { 'Content-Type': 'application/json', 'x-cui-eingabe': 'mensch' },
        body: JSON.stringify({ accountId: selectedId, sessionId, message: msg, workDir, projectId }),
        signal: AbortSignal.timeout(SEND_TIMEOUT_MS),
      });
        const locked = response.status === 409 && (await response.clone().json().catch(() => ({}))).sendInFlight === true;
        if (!locked || attempt >= SEND_LOCK_RETRY_MS.length) { resp = response; break; }
        // Keep ownership of this panel's queue throughout backoff. Releasing it
        // here let a later message race the retry and overtake the earlier text.
        await new Promise(resolve => setTimeout(resolve, SEND_LOCK_RETRY_MS[attempt]));
      }

      usage('message_request_result', { session_id: sessionId, panel_id: panelId, status: resp.status });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }));
        if (resp.status === 503 && ((errData as any).sessionAliveButUnreachable || (errData as any).retryLater)) {
          // retryLater: die Buehnenzuordnung war nur voruebergehend nicht
          // erreichbar (Haus Partner, 21.09.2026) — ebenfalls nichts zu
          // entscheiden, schon gar keine Fortsetzung anbieten.
          // Server proved (OS-level check) the old process is still alive —
          // just not reachable right now. Forking here would race a second
          // process against the same still-running one, so the server
          // refuses on purpose. Nothing to decide: keep the text, say so,
          // let the user retry in a few seconds.
          setInput(msg);
          const note: Message = { role: 'system', content: `${errData.error || 'Session ist noch aktiv, aber gerade nicht erreichbar.'} Dein Text steht wieder im Eingabefeld.`, timestamp: new Date().toISOString() };
          pushPendingSystemMsg(note);
          setMessages(prev => trimMessages([...prev, note]));
        } else if (resp.status === 409 && (errData as any).buehnenzuordnungAbgewiesen) {
          // Die Bühnenzuordnung widerspricht (HS4F, 10.10.2026) — die Session ist nicht tot,
          // also keine Fortsetzungs-Rückfrage: Grund zeigen, Text zurück ins Eingabefeld.
          setInput(msg);
          const note: Message = { role: 'system', content: `${errData.error || 'Die Bühnenzuordnung widerspricht.'} Dein Text steht wieder im Eingabefeld.`, timestamp: new Date().toISOString() };
          pushPendingSystemMsg(note);
          setMessages(prev => trimMessages([...prev, note]));
        } else if (resp.status === 409 && (errData as any).resumeFailedNeedsApproval) {
          // Resume gescheitert, alter Prozess nachweislich tot. Eine
          // Fortsetzung ist eine NEUE Session, die diesen Auftrag erbt —
          // deshalb fragt der Server, statt sie still zu starten. Sagt der
          // Nutzer ja, uebergibt der Server in einem Zug: Titel wandert mit,
          // die alte Konversation wird geschlossen. Sagt er nein, bleibt der
          // Text im Eingabefeld und es entsteht gar nichts.
          const approved = window.confirm(`${errData.error || 'Diese Session laesst sich nicht fortsetzen.'}\n\nFortsetzung jetzt starten?`);
          if (!approved) {
            setInput(msg);
            const note: Message = { role: 'system', content: 'Fortsetzung abgelehnt — es wurde keine neue Session gestartet. Dein Text steht wieder im Eingabefeld.', timestamp: new Date().toISOString() };
            pushPendingSystemMsg(note);
            setMessages(prev => trimMessages([...prev, note]));
          } else {
            const forkResp = await fetch('/api/mission/send', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'x-cui-eingabe': 'mensch' },
              body: JSON.stringify({ accountId: selectedId, sessionId, message: msg, workDir, projectId, forkApproved: true }),
              signal: AbortSignal.timeout(SEND_TIMEOUT_MS),
            });
            const forkData = await forkResp.json().catch(() => ({ error: `HTTP ${forkResp.status}` }));
            if (forkResp.ok && forkData.sessionId) {
              setSessionId(forkData.sessionId);
              if (persistSession) try { localStorage.setItem(getSessionKey(selectedId), forkData.sessionId); } catch {} // silent-ok: localStorage may be disabled
              onRouteChange?.(`/c/${forkData.sessionId}`);
              setMessages([{ role: 'system', content: 'Fortsetzung gestartet — die vorherige Konversation wurde abgeschlossen.', timestamp: new Date().toISOString() }, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
            } else {
              setInput(msg);
              const errMsg: Message = { role: 'system', content: `Fehler: ${forkData.error || 'Fortsetzung fehlgeschlagen'}`, timestamp: new Date().toISOString() };
              pushPendingSystemMsg(errMsg);
              setMessages(prev => trimMessages([...prev, errMsg]));
            }
          }
        } else if (resp.status === 409 && (errData as any).sendInFlight) {
          // Automatische Versuche erschoepft — jetzt WIRKLICH sichtbar machen
          // und den Text zurueckgeben, statt ihn nur in einer Notiz zu erwaehnen.
          setInput(msg);
          const note: Message = { role: 'system', content: `${errData.error || 'Fuer diese Session laeuft bereits ein Senden.'} Auch nach ${SEND_LOCK_RETRY_MS.length} automatischen Versuchen ohne Erfolg — dein Text steht wieder im Eingabefeld, bitte manuell erneut senden.`, timestamp: new Date().toISOString() };
          pushPendingSystemMsg(note);
          setMessages(prev => trimMessages([...prev, note]));
        } else {
        const errMsg: Message = { role: 'system', content: `Fehler: ${errData.error || 'Senden fehlgeschlagen'}`, timestamp: new Date().toISOString() };
        pushPendingSystemMsg(errMsg); // Survives poll overwrites
        setMessages(prev => trimMessages([...prev, errMsg]));
        }
      } else {
        const data = await resp.json().catch(() => ({})); // silent-ok: send response parse failure uses empty object; resume-failed detection skipped
        // Server auto-recovered from broken resume → switch to new session
        if (data.resumeFailed && data.sessionId) {
          console.log(`[CuiLite] Resume failed, switched to new session: ${data.sessionId}`);
          setSessionId(data.sessionId);
          if (persistSession) try { localStorage.setItem(getSessionKey(selectedId), data.sessionId); } catch {} // silent-ok: localStorage may be disabled
          onRouteChange?.(`/c/${data.sessionId}`);
          setMessages([{ role: 'system', content: 'Neue Session gestartet (alte Session konnte nicht fortgesetzt werden)', timestamp: new Date().toISOString() }, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
        }
      }
    } catch (err) {
      console.error('[CuiLite] Send error:', err);
      const errText = err instanceof DOMException && err.name === 'TimeoutError'
        ? `Keine Antwort innerhalb von ${SEND_TIMEOUT_MS / 1000}s`
        : String(err);

      // Frueher wurde hier pauschal "nicht angekommen" angenommen und der Text
      // ins Feld zurueckgeschrieben. Das ist eine Behauptung, die der Client
      // nicht belegen kann — und sie ist teuer: der Nutzer haelt das fuer ein
      // nicht geleertes Eingabefeld, drueckt erneut, und die Nachricht laeuft
      // ein zweites Mal (am 16.08.2026 drei wortgleiche Meldungen in 90s).
      // Deshalb wird jetzt nachgesehen statt geraten.
      const delivered = await confirmDelivery(sessionId, msg);
      usage('message_request_result', { session_id: sessionId, panel_id: panelId, reason: delivered === true ? 'confirmed_after_error' : delivered === false ? 'not_delivered' : 'unknown' });

      let note: string;
      if (delivered === true) {
        // Angekommen — Feld bleibt leer, sonst provoziert man das Doppelsenden.
        note = `${errText}. Die Nachricht ist aber nachweislich angekommen und wird bearbeitet — die Antwort erscheint von selbst. Nicht erneut senden.`;
      } else if (delivered === false) {
        // Belegt nicht angekommen: Text zurueckgeben ist hier korrekt.
        note = `${errText}. Die Nachricht ist nicht angekommen — dein Text steht wieder im Eingabefeld.`;
        setInput(msg);
      } else {
        // Unbekannt. Nichts behaupten, nichts stillschweigend zurueckschreiben:
        // ein erneutes Senden koennte doppelt laufen, das muss der Nutzer wissen.
        note = `${errText}, und der Zustand liess sich nicht pruefen (Server nicht erreichbar). `
          + 'Die Nachricht KANN angekommen sein — erneutes Senden wuerde sie doppelt ausfuehren. '
          + `Dein Text ist nicht verloren:\n\n${msg}`;
      }

      const errMsg: Message = { role: 'system', content: note, timestamp: new Date().toISOString() };
      pushPendingSystemMsg(errMsg);
      setMessages(prev => trimMessages([...prev, errMsg]));
    } finally {
      sendInFlightRef.current = false;
    }
    setIsLoading(false);
    // Invalidate hash so next poll always applies state (user just sent a message)
    lastPollHashRef.current = '';
    // Mark as working immediately (WS turn-update will deliver content in real-time)
    setIsAgentDone(false); // Reset so WS events can set working again
    setAttention('working');
    setTimeout(pollNow, 1000);

    // Warteschlange leeren: waehrend DIESER Sendung liefen, koennten weitere
    // Nachrichten eingereiht worden sein (handleSend haengt sie an, wenn
    // sendInFlightRef schon besetzt war). Sichtbar gereiht heisst auch:
    // automatisch zugestellt, sobald frei — nicht liegen lassen.
    if (sendQueueRef.current.length > 0) {
      const next = sendQueueRef.current.shift()!;
      setPendingSendCount(sendQueueRef.current.length);
      try {
        await runSend(next);
      } catch (e) {
        // Darf NIE dazu fuehren, dass die Nachricht wortlos verschwindet.
        console.error('[CuiLite] Automatisches Zustellen der eingereihten Nachricht fehlgeschlagen:', e);
        const errMsg: Message = { role: 'system', content: `Automatisches Senden der eingereihten Nachricht ist fehlgeschlagen: ${e}. Dein Text ist nicht verloren:\n\n${next}`, timestamp: new Date().toISOString() };
        pushPendingSystemMsg(errMsg);
        setMessages(prev => trimMessages([...prev, errMsg]));
      }
    }
  }, [sessionId, selectedId, workDir, projectId, pollNow, onRouteChange, confirmDelivery]);

  const letzteVorgabe = useRef(0);
  useEffect(() => {
    if (!eingabeVorgabe || eingabeVorgabe.nr === letzteVorgabe.current) return;
    letzteVorgabe.current = eingabeVorgabe.nr;
    setInput(alt => (alt.trim() ? `${eingabeVorgabe.text}\n${alt}` : eingabeVorgabe.text));
    textareaRef.current?.focus();
  }, [eingabeVorgabe]);
  const handleSend = useCallback(async (overrideMessage?: string) => {
    const rawMsg = overrideMessage || input.trim();
    if (!rawMsg) return;
    // Ein eindeutiger Kurzbefehl (Ansicht oben setzen) braucht kein Modell: er wird hier verbraucht, das Feld einmal geleert, nichts gesendet und nichts doppelt.
    if (!overrideMessage && !planMode && vorSenden?.(rawMsg)) { setInput(''); return; }
    usage('message_submit', { session_id: sessionId || undefined, panel_id: panelId, stage_id: panelId?.startsWith('guide-berater-') ? panelId.slice('guide-berater-'.length) : undefined });
    // Ohne gebundene Sitzung ging das Senden bisher WORTLOS ins Leere: der Text
    // blieb stehen, weil setInput('') weiter unten nie erreicht wurde. Von aussen
    // sieht das aus wie "das Eingabefeld leert sich nicht" — genau so wurde es
    // am 16.08.2026 viermal gemeldet, obwohl gar nichts gesendet wurde. Der Text
    // bleibt absichtlich erhalten (nichts geht verloren), aber der Grund wird jetzt
    // gesagt statt verschluckt.
    if (!sessionId) {
      setMessages(prev => trimMessages([...prev, {
        role: 'system',
        content: 'Keine Sitzung verbunden — die Nachricht wurde NICHT gesendet. Starte oben eine neue Konversation oder waehle eine bestehende aus; dein Text bleibt erhalten.',
        timestamp: new Date().toISOString(),
      }]));
      return;
    }
    if (window.__cuiServerAlive === false) {
      setMessages(prev => trimMessages([...prev, { role: 'system', content: 'Server nicht erreichbar — bitte warten bis Verbindung wiederhergestellt ist.', timestamp: new Date().toISOString() }]));
      return;
    }
    // User is actively sending — unfinish if session was manually finished
    doUnfinish(sessionId);
    const msg = (!overrideMessage && planMode) ? `Bitte verwende Plan-Modus: ${rawMsg}` : rawMsg;
    if (!overrideMessage) setInput('');
    if (!overrideMessage && planMode) setPlanMode(false);
    setMessages(prev => trimMessages([...prev, { role: 'user', content: msg, timestamp: new Date().toISOString() }]));
    // User action → always scroll to bottom
    userScrolledUpRef.current = false;
    setUnreadCount(0);
    setTimeout(() => scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' }), 50);
    setIsAgentDone(false);
    setAttentionReason(undefined);
    setRateLimitMessage(null);
    // Tippen und Absenden duerfen NIE davon abhaengen, ob die Sitzung gerade
    // beschaeftigt ist (Rafael, 12.09.2026 — "ueberall instant reagieren"). Die
    // Nachricht steht oben bereits sichtbar im Transkript und das Feld ist schon
    // leer; laeuft bereits ein anderer Send fuer dieses Panel (Doppel-Enter,
    // Doppelklick, oder einfach ein zweiter Send waehrend der erste noch nicht
    // zurueck ist), wird NICHT mehr wortlos verworfen (frueher: console.warn +
    // return, Vorfall 25.08.2026) — stattdessen sichtbar gereiht. runSend leert
    // die Queue automatisch, sobald der laufende Send fertig ist.
    if (sendInFlightRef.current) {
      sendQueueRef.current.push(msg);
      setPendingSendCount(sendQueueRef.current.length);
      return;
    }
    await runSend(msg);
  }, [input, sessionId, planMode, doUnfinish, runSend, vorSenden]);

  // Respond to tool_use blocks (AskUserQuestion, ExitPlanMode, EnterPlanMode)
  // Antwort auf AskUserQuestion/ExitPlanMode/EnterPlanMode. Teilt sich die
  // Zustell-Logik (inkl. Warteschlange + Lock-Retry) mit handleSend ueber
  // runSend — vorher hatte dieser Pfad eine eigene Kopie des /send-Codes und
  // seinen eigenen stillen Verwurf ("Doppelklick" console.warn, kein Hinweis
  // fuers Auge), obwohl der Fall identisch ist: ein Klick, waehrend fuer dieses
  // Panel schon eine andere Sendung laeuft. Zwei Kopien derselben Regel drifteten
  // genau so auseinander (hier fehlte z.B. der Lock-Retry, den handleSend jetzt hat).
  const handleRespond = useCallback(async (text: string) => {
    if (!sessionId) return;
    if (window.__cuiServerAlive === false) {
      console.warn('[CuiLite] Respond blocked: server not alive');
      return;
    }
    setMessages(prev => trimMessages([...prev, { role: 'user', content: text, timestamp: new Date().toISOString() }]));
    if (sendInFlightRef.current) {
      sendQueueRef.current.push(text);
      setPendingSendCount(sendQueueRef.current.length);
      return;
    }
    await runSend(text);
  }, [sessionId, runSend]);

  const handlePermission = useCallback(async (permId: string, action: 'approve' | 'deny') => {
    if (window.__cuiServerAlive === false) {
      console.warn('[CuiLite] Permission blocked: server not alive');
      return;
    }
    try {
      await fetch(`/api/mission/permissions/${selectedId}/${permId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
        signal: AbortSignal.timeout(10000),
      });
      lastPollHashRef.current = '';
      setTimeout(pollNow, 500);
    } catch (err) {
      console.error('[CuiLite] Permission error:', err);
    }
  }, [selectedId, pollNow]);

  const handleStop = useCallback(async () => {
    if (!sessionId) return;
    if (window.__cuiServerAlive === false) {
      console.warn('[CuiLite] Stop blocked: server not alive');
      return;
    }
    try {
      const resp = await fetch(`/api/mission/conversation/${selectedId}/${sessionId}/stop`, { method: 'POST', signal: AbortSignal.timeout(10000) });
      const data = await resp.json().catch(() => ({})); // silent-ok: stop response parse failure; stop status check uses falsy defaults
      setAttention('idle');
      setAttentionReason('done');
      setConvStatus('completed');
      lastPollHashRef.current = '';
      if (!data.apiStopOk && !data.childrenKilled) {
        console.warn('[CuiLite] Stop: API stop failed and no children killed — agent may still be running');
      }
      console.log(`[CuiLite] Stop: apiStopOk=${data.apiStopOk}, streamingId=${data.streamingId?.slice(0,8)}, killed=${data.childrenKilled}`);
      setTimeout(pollNow, 500);
    } catch (err) {
      console.error('[CuiLite] Stop error:', err);
    }
  }, [sessionId, selectedId, pollNow]);

  const handleHardKill = useCallback(async () => {
    if (!sessionId) return;
    if (window.__cuiServerAlive === false) return;
    if (!confirm('HARD KILL — Alle Prozesse dieser Session sofort beenden?')) return;
    try {
      const resp = await fetch(`/api/mission/conversation/${sessionId}/hard-kill`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        signal: AbortSignal.timeout(15000),
      });
      const data = await resp.json().catch(() => ({})); // silent-ok: hard-kill response parse failure; processes are killed regardless
      setAttention('idle');
      setAttentionReason('done');
      setConvStatus('completed');
      console.log(`[CuiLite] Hard-kill: ${data.killed} processes killed`);
      setTimeout(pollNow, 500);
    } catch (err) {
      console.error('[CuiLite] Hard-kill error:', err);
    }
  }, [sessionId, pollNow]);

  const handleQueueNavigate = useCallback((sid: string) => {
    setSessionId(sid);
    setShowQueue(false);
    setMessages([]);
    setPermissions([]);
    setAttention('idle');
    setAttentionReason(undefined);
            setRateLimitMessage(null);
    if (persistSession) try { localStorage.setItem(getSessionKey(selectedId), sid); } catch {} // silent-ok: localStorage may be disabled
    onRouteChange?.(`/c/${sid}`);
  }, [onRouteChange, selectedId, persistSession]);

  const handleStartNew = useCallback(async (subject: string, message: string, model: string = 'fable') => {
    if (window.__cuiServerAlive === false) {
      console.warn('[CuiLite] Start new blocked: server not alive');
      return false;
    }
    // Reserve panel immediately so auto-sync doesn't treat it as empty during POST
    onRouteChange?.('/c/_starting');
    try {
      const resp = await fetch('/api/mission/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-cui-eingabe': 'mensch' },
        // clientId: nur DIESES Fenster wird nach dem Start in den Ziel-Workspace
        // geschaltet. Ohne sie aktiviert der Server niemanden (siehe mission.ts).
        body: JSON.stringify({ accountId: selectedId || 'auto', message, workDir, subject, model, projectId,
          clientId: (window as unknown as { __CUI_CLIENT_ID__?: string }).__CUI_CLIENT_ID__ }),
        signal: AbortSignal.timeout(SEND_TIMEOUT_MS),
      });
      if (!resp.ok) {
        console.warn('[CuiLite] Start new HTTP error:', resp.status);
        onRouteChange?.('');
        return false;
      }
      const data = await resp.json().catch(() => null); // silent-ok: start-new response parse failure returns null; warning logged and false returned
      if (!data || !data.sessionId) {
        console.warn('[CuiLite] Start new: invalid response (missing sessionId)');
        onRouteChange?.('');
        return false;
      }
      setSessionId(data.sessionId);
      setBuehnenFehler(typeof data.eigene_buehne_fehler === 'string' ? data.eigene_buehne_fehler : null);
      setShowQueue(false);
      setMessages([]);
      if (persistSession) try { localStorage.setItem(getSessionKey(selectedId), data.sessionId); } catch {} // silent-ok: localStorage may be disabled
      onRouteChange?.(`/c/${data.sessionId}`);
      return true;
    } catch (err) {
      console.warn('[CuiLite] Start new error:', err);
      onRouteChange?.('');
      return false;
    }
  }, [selectedId, workDir, onRouteChange, persistSession]);

  // handleBack removed — conversations stay mounted until Finish

  const handlePause = useCallback(async () => {
    if (!sessionId) return;
    const newPaused = !isPaused;
    try {
      await fetch(`/api/mission/conversation/${sessionId}/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-cui-eingabe': 'mensch' },
        body: JSON.stringify({ paused: newPaused }),
        signal: AbortSignal.timeout(5000),
      });
      setIsPaused(newPaused);
    } catch (e) { console.warn('[CuiLite] Pause error:', e); }
  }, [sessionId, isPaused]);

  const handleReview = useCallback(async () => {
    if (!sessionId || reviewState === 'running') return;
    try {
      setReviewState('running');
      const resp = await fetch(`/api/mission/conversation/${sessionId}/review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
        signal: AbortSignal.timeout(15000),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: 'unknown' }));
        console.warn('[CuiLite] Review error:', err);
        setReviewState('idle');
      }
    } catch (e) {
      console.warn('[CuiLite] Review error:', e);
      setReviewState('idle');
    }
  }, [sessionId, reviewState]);

  const handleFinish = useCallback(async () => {
    if (!sessionId) return;
    try {
      const resp = await fetch(`/api/mission/conversation/${sessionId}/finish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-cui-eingabe': 'mensch' },
        body: JSON.stringify({ finished: true }),
        signal: AbortSignal.timeout(20000),
      });
      if (resp.status === 409) {
        // Session still has a live process — confirm and retry with force
        const data = await resp.json().catch(() => ({ message: 'Session laeuft noch.' }));
        if (confirm(`${data.message}\n\nTrotzdem finishen?`)) {
          const r2 = await fetch(`/api/mission/conversation/${sessionId}/finish`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-cui-eingabe': 'mensch' },
            body: JSON.stringify({ finished: true, confirm: true }),
            signal: AbortSignal.timeout(20000),
          });
          if (!r2.ok) { console.warn('[CuiLite] Finish confirm failed:', r2.status); return; }
        } else {
          return; // User cancelled — don't call onFinish
        }
      } else if (!resp.ok) {
        console.warn('[CuiLite] Finish failed:', resp.status);
        return;
      }
      // Server broadcasts control:conversation-finished -> LayoutManager deleteTab
      // onFinish as immediate local fallback
      onFinish?.(sessionId);
    } catch (e) { console.warn('[CuiLite] Finish error:', e); }
  }, [sessionId, onFinish]);

  // Chat-Kopf aus derselben Messung wie Raum und Karte (chatKopfTaetigkeit.ts, Rafael 29.09.2026).
  const [gemessen, setGemessen] = useState<GemesseneTaetigkeit | null>(null);
  const taetigkeitGesperrtRef = useRef(false);
  useEffect(() => {
    setGemessen(null);
    if (!sessionId || !betreiberWege || taetigkeitGesperrtRef.current) return;
    let aus = false;
    const sid = sessionId;
    const messe = () => {
      if (window.__cuiServerAlive !== true) return;
      fetch(`/api/guide/sitzungen-hier/taetigkeit/${encodeURIComponent(sid)}`, { signal: AbortSignal.timeout(15000) })
        .then(async r => {
          if (aus) return;
          if (r.status === 403) { taetigkeitGesperrtRef.current = true; setGemessen(null); return; } // Nicht-Admin: keine Messung, Kopf zeigt das Register
          if (!r.ok) { setGemessen(null); return; } // 404 = anderes Haus: dieser Rechner kann nichts ueber sie sagen
          const d = await r.json() as { sitzung?: string; taetigkeit?: GemesseneTaetigkeit };
          if (!aus && d.sitzung === sid) setGemessen(d.taetigkeit ?? null);
        })
        .catch(() => { if (!aus) setGemessen(null); });
    };
    // Jede Registerbewegung loest eine Nachmessung aus — der Kopf folgt der Messung, nicht dem Register.
    const kurz = setTimeout(messe, 800);
    const takt = setInterval(messe, 15000);
    return () => { aus = true; clearTimeout(kurz); clearInterval(takt); };
  }, [sessionId, attention, attentionReason, betreiberWege]);
  const kopf = kopfAusTaetigkeit(gemessen);
  const kopfAttention = kopf ? kopf.attention : (attention === 'working' ? 'idle' : attention);
  const kopfReason = kopf ? kopf.reason : (attention === 'working' ? 'unbekannt' : attentionReason);
  const kopfFertig = kopf ? kopf.attention !== 'working' : isAgentDone;

  // --- Render ---
  // Dim panels that are busy (Claude working), blocked (rate limit), or paused —
  // nothing for the user to do there. "Your turn" states (needs_attention plan/
  // question/permission/error, idle+done, plain idle) stay bright. Hover or pin overrides.
  const busyOrBlocked = kopfAttention === 'working'
    || (kopfAttention === 'needs_attention' && kopfReason === 'rate_limit')
    || isPaused;
  const shouldDim = !schnellchatOffen && !!sessionId && busyOrBlocked && !dimPinned && !isDimHovered;
  return (
    <div
      className={isPaused ? 'cui-panel-border--paused' : kopfAttention === 'working' ? 'cui-panel-border--working' : kopfAttention === 'needs_attention' ? 'cui-panel-border--attention' : ''}
      style={{ position: 'relative', display: 'flex', flexDirection: 'column', height: '100%', background: isPaused ? 'rgba(122,162,247,0.13)' : 'var(--tn-surface)', overflow: 'hidden', opacity: shouldDim ? 0.4 : 1, filter: shouldDim ? 'grayscale(0.55)' : 'none', transition: 'opacity 0.18s ease, filter 0.18s ease' }}
      onClick={sessionId ? () => doUnfinish(sessionId) : undefined}
      onMouseEnter={() => setIsDimHovered(true)}
      onMouseLeave={() => setIsDimHovered(false)}
    >
      {/* Header */}
      <div title={kopf ? `Gemessen: ${kopf.wort}` : undefined} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px',
        background: (kopfAttention === 'working' && sessionId) ? 'var(--tn-kopf-arbeitet)' : (kopfAttention === 'needs_attention' && sessionId) ? (kopfReason === 'rate_limit' ? 'var(--tn-kopf-limit)' : 'var(--tn-kopf-fertig)') : (kopfAttention === 'idle' && kopfReason === 'done' && sessionId) ? 'var(--tn-kopf-fertig)' : (kopfAttention === 'idle' && kopfReason === 'background' && sessionId) ? 'var(--tn-kopf-hintergrund)' : 'var(--tn-bg-dark)', borderBottom: (kopfAttention === 'working' && sessionId) ? '2px solid var(--tn-green)' : (kopfAttention === 'needs_attention' && sessionId) ? (kopfReason === 'rate_limit' ? '2px solid var(--tn-fehler)' : '2px solid var(--tn-orange-hell)') : (kopfAttention === 'idle' && kopfReason === 'done' && sessionId) ? '2px solid var(--tn-orange-hell)' : (kopfAttention === 'idle' && kopfReason === 'background' && sessionId) ? '2px solid var(--tn-gelb)' : '1px solid var(--tn-border)',
        height: 30, flexShrink: 0,
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: kopfAttention === 'working' ? 'var(--tn-green)'
            : kopfAttention === 'needs_attention' ? 'var(--tn-orange-hell)'
            : (kopfFertig && kopfReason === 'done') ? 'var(--tn-orange-hell)'
            : (kopfFertig && kopfReason === 'background') ? 'var(--tn-gelb)'
            : convStatus === 'ongoing' ? 'var(--tn-dim)'
            : account.color,
          animation: kopfAttention === 'working' ? 'q-pulse 2s ease-in-out infinite' : undefined,
        }} />
        {simpleMode && (
          <button
            data-ai-id="cui-lite-show-chat-list"
            onClick={(e) => { e.stopPropagation(); setShowQueue(true); }}
            title="Alle meine Chats"
            style={{
              background: showQueue ? 'var(--tn-blue)' : 'var(--tn-bg)',
              color: showQueue ? '#fff' : 'var(--tn-text)',
              border: '1px solid var(--tn-border)', borderRadius: 4,
              padding: '2px 8px', fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            ☰ Meine Chats
          </button>
        )}
        {sessionId && (user?.role === 'admin' || authEnabled === false) && <Schnellchat key={sessionId} parent={sessionId} buehneId={buehneId} onOffen={setSchnellchatOffen} />}
        {/* EIN TITEL: der des Aufrufers (Coach-Spalte) oder der Konversationsname.
            Frueher stand der Name als eigene Zeile UNTER dem Kopf und der Aufrufer
            setzte noch einmal einen Kopf darueber — bis zu drei Titelzeilen. */}
        {sessionId && (titel || convName) && (
          <span data-ai-id="cui-lite-titel" title={titel || convName || undefined} style={{
            fontSize: 12, fontWeight: 600, color: 'var(--tn-text)', minWidth: 0, flex: '0 1 auto',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{titel || convName}</span>
        )}
        {!sessionId && titel && (
          <span data-ai-id="cui-lite-titel" style={{ fontSize: 12, fontWeight: 600, color: 'var(--tn-text)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titel}</span>
        )}

        {/* Ohne aktuelle Messung keine sichere Arbeitsbehauptung aus dem Register. */}
        {sessionId && !kopf && attention === 'working' && <span style={{ fontSize: 9, color: 'var(--tn-orange)' }}>Status wird geprüft</span>}
        {deliveryStatus && deliveryStatus.pending > 0 && (
          <span role="status" style={{ fontSize: 10, color: 'var(--tn-orange)' }}
            title="Gesichert, aber vom KI-Prozess noch nicht bestätigt. Ein laufendes Werkzeug wird nicht abgebrochen.">
            {deliveryStatus.delayed ? 'Keine bestätigte Übernahme – bitte prüfen' : `${deliveryStatus.pending} Nachricht(en) warten auf Übernahme`}
          </span>
        )}
        {/* Siehe uebernahmeHaengt.ts: nur bei echtem Haengen, nur auf ausdruecklichen Klick. */}
        {uebernahmeHaengt(deliveryStatus) && (
          <button type="button" data-ai-id="cui-lite-neu-anstossen"
            onClick={() => {
              if (!confirm('Diese Sitzung nimmt seit über 10 Minuten keine Nachricht mehr an.\n\n'
                + 'Sie wird jetzt angehalten. Der bisherige Verlauf bleibt erhalten. '
                + 'Schick danach deine letzte Nachricht noch einmal — die Sitzung startet damit frisch.\n\n'
                + 'This session has not accepted messages for over 10 minutes. It will be stopped; '
                + 'then send your last message again and it starts fresh.')) return;
              void handleStop();
            }}
            title="Hält die hängende Sitzung an. Danach die letzte Nachricht erneut senden."
            style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, cursor: 'pointer',
              background: 'var(--tn-bg)', border: '1px solid var(--tn-orange)', color: 'var(--tn-orange)' }}>
            Sitzung neu anstoßen
          </button>
        )}
        {/* Status badge */}
        {kopfAttention === 'working' && sessionId && !deliveryStatus?.delayed && (
          <span style={{ fontSize: 9, color: 'var(--tn-green)', fontWeight: 600, opacity: 0.8, display: 'flex', alignItems: 'center', gap: 3, overflow: 'hidden', maxWidth: 220 }}>
            <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: 'var(--tn-green)', animation: 'q-pulse 1s ease-in-out infinite', flexShrink: 0 }} />
            {currentTool ? (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={currentTool.toolDetail || currentTool.toolName}>
                {currentTool.toolName}{currentTool.toolDetail ? `: ${currentTool.toolDetail}` : ''}
              </span>
            ) : 'arbeitet'}
          </span>
        )}
        {isPaused && sessionId && (
          <span style={{ fontSize: 9, color: 'var(--tn-link)', fontWeight: 600, letterSpacing: '0.3px' }}>Pausiert</span>
        )}
        {!isPaused && kopfAttention === 'idle' && kopfFertig && sessionId && kopfReason === 'done' && !manualFinished && (
          <span style={{ fontSize: 9, color: 'var(--tn-orange-hell)', fontWeight: 600 }}>Wartet</span>
        )}
        {!isPaused && kopfAttention === 'idle' && kopfFertig && sessionId && kopfReason === 'background' && !manualFinished && (
          <span style={{ fontSize: 9, color: 'var(--tn-gelb)', fontWeight: 600 }} title="Hintergrundlauf aktiv — die Session meldet sich selbst, wenn er fertig ist">Hintergrund</span>
        )}
        {!isPaused && kopfAttention === 'needs_attention' && sessionId && (
          <span style={{ fontSize: 9, color: kopfReason === 'rate_limit' ? 'var(--tn-fehler)' : 'var(--tn-orange-hell)', fontWeight: 600 }}>
            {kopfReason === 'plan' ? 'Plan' : kopfReason === 'question' ? 'Frage' : kopfReason === 'context_overflow' ? 'Zu lang' : kopfReason === 'rate_limit' ? 'Rate Limit' : kopfReason === 'error' || kopfReason === 'send_failed' ? 'Fehler' : kopfReason === 'done' ? 'Wartet' : 'Aktion'}
          </span>
        )}



        {/* Mount-sync indicator: shown until first poll clears stale cache */}
        {isMountSyncing && sessionId && (
          <span style={{ fontSize: 9, color: 'var(--tn-link)', fontWeight: 600, opacity: 0.75 }}>syncing…</span>
        )}

        {/* Abstand: links Titel und Zustand, rechts Zugang, Stopp, Menue */}
        <span style={{ flex: 1 }} />

        {/* Laufende Hinweise, die sonst nur im Menue zu sehen waeren. */}
        {sessionId && planMode && isAdvancedUser && (
          <span style={{ fontSize: 9, color: 'var(--tn-warn)', fontWeight: 700, flexShrink: 0 }} title="Plan-Modus ist an (⋯)">Plan</span>
        )}
        {sessionId && loopEnabled && (
          <span style={{ fontSize: 9, color: 'var(--tn-ok)', fontWeight: 700, flexShrink: 0 }} title="Loop laeuft (⋯)">Loop ●</span>
        )}
        {/* KI-Zugang klein; gewechselt wird im Menue. */}
        {!simpleMode && !kompakt && (
          <button
            data-ai-id="cui-lite-zugang"
            onClick={(e) => { e.stopPropagation(); setMenueOffen(true); }}
            title="KI-Zugang dieses Chats · Wechseln im ⋯-Menue"
            style={{ padding: 0, fontSize: 10, background: 'none', border: 'none', color: 'var(--tn-text-muted)', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {accountAccessLabel(selectedId)}
          </button>
        )}
        {kopfZusatz && <span style={{ display: 'inline-flex', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>{kopfZusatz}</span>}

        {/* NOTHALT. Die Aktionszeile liegt im Menue - der Stopp darf es nicht,
            solange etwas laeuft. Nur dann steht er hier, und nur ausserhalb der
            harten Form (die Guide-Spalte bleibt, wie sie war). */}
        {!kompakt && sessionId && busyOrBlocked && (
          <button
            data-ai-id="cui-lite-stop-minimal"
            onClick={(e) => { e.stopPropagation(); handleStop(); }}
            title="Konversation stoppen"
            style={{ padding: '1px 7px', fontSize: 10, background: 'rgba(239,68,68,0.15)', border: '1px solid var(--tn-fehler)', borderRadius: 3, color: 'var(--tn-fehler)', cursor: 'pointer', flexShrink: 0 }}
          >
            Stop
          </button>
        )}
        {(!kompakt || menueZusatz) && (
          <button
            data-ai-id="cui-lite-detail-toggle"
            onClick={(e) => { e.stopPropagation(); setMenueOffen(o => !o); }}
            aria-expanded={menueOffen}
            aria-controls={`cui-lite-menue-${panelId || 'x'}`}
            title={menueOffen ? 'Menue schliessen' : 'Mehr: KI-Zugang, Live/Refresh/Review, Plan/Loop/KILL, Kurzprompts, Diktat …'}
            style={{ padding: '0 8px', fontSize: 14, lineHeight: '18px', background: menueOffen ? 'var(--tn-bg-highlight)' : 'transparent', border: '1px solid var(--tn-border)', borderRadius: 3, color: 'var(--tn-text-muted)', cursor: 'pointer', flexShrink: 0 }}
          >
            {menueOffen ? '\u2715' : '\u22EF'}
          </button>
        )}
      </div>

      {/* ⋯-MENUE: die ganze Werkstatt an EINER Stelle, im Fluss unter der Kopfzeile.
          Nichts davon ist entfernt, nur umgezogen (Rafael, 03.10.2026, s. menueOffen). */}
      {menueOffen && (
        <div
          id={`cui-lite-menue-${panelId || 'x'}`}
          data-ai-id="cui-lite-menue"
          role="region"
          aria-label="Chat-Werkzeuge"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => { if (e.key === 'Escape') setMenueOffen(false); }}
          style={{ flexShrink: 0, maxHeight: '45%', overflowY: 'auto', padding: '6px 8px', background: 'var(--tn-bg-dark)', borderBottom: '1px solid var(--tn-border)', display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          {menueZusatz && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>{menueZusatz}</div>}
          {!kompakt && (<>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        {!simpleMode && (
        <select
          data-ai-id="cui-lite-account-select"
          aria-label="KI-Zugang wechseln"
          title="KI-Zugang wechseln · Automatisch waehlt verfuegbare Kapazitaet"
          value={selectedId}
          onChange={(e) => {
            const newAcct = e.target.value;
            // Cancel in-flight polling before switching
            if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }

            if (sessionId) {
              // Chat is open — reassign conversation to new account, stay in chat.
              //
              // Der Wechsel unten wird OPTIMISTISCH angezeigt, aber der Server darf ihn
              // ablehnen: 400 'unknown account', 409 'Anbieterwechsel erfordert eine neue
              // Unterhaltung...', 503 'no account available'. Bis 19.09.2026 wurde die
              // Antwort nie gelesen (nur .catch auf den Netzfehler) — ein abgelehnter
              // Wechsel sah auf dem Schirm exakt aus wie ein gelungener, bis zum naechsten
              // Nachladen. Deshalb: Zustand vorher sichern und bei !res.ok zuruecknehmen.
              const sid = sessionId;
              const prevAcct = selectedId;
              const prevAttention = kopfAttention;
              const prevAttentionReason = kopfReason;
              const prevRateLimitMessage = rateLimitMessage;
              let prevKontoFuerSitzung: string | null = null;
              let prevSessionFuerNeu: string | null = null;
              try { prevKontoFuerSitzung = localStorage.getItem(kontoFuerSitzungKey(sid)); } catch {} // silent-ok: localStorage may be disabled
              try { prevSessionFuerNeu = localStorage.getItem(getSessionKey(newAcct)); } catch {} // silent-ok: localStorage may be disabled

              const meldeFehler = (text: string, zuruecknehmen: boolean) => {
                if (zuruecknehmen) {
                  setSelectedId(prevAcct);
                  setAttention(prevAttention);
                  setAttentionReason(prevAttentionReason);
                  setRateLimitMessage(prevRateLimitMessage);
                  try { localStorage.setItem(storageKey, prevAcct); } catch {} // silent-ok: localStorage may be disabled
                  try {
                    if (prevKontoFuerSitzung === null) localStorage.removeItem(kontoFuerSitzungKey(sid));
                    else localStorage.setItem(kontoFuerSitzungKey(sid), prevKontoFuerSitzung);
                  } catch {} // silent-ok: localStorage may be disabled
                  if (persistSession) {
                    try {
                      if (prevSessionFuerNeu === null) localStorage.removeItem(getSessionKey(newAcct));
                      else localStorage.setItem(getSessionKey(newAcct), prevSessionFuerNeu);
                    } catch {} // silent-ok: localStorage may be disabled
                  }
                }
                const zeile: Message = { role: 'api_error', content: text, timestamp: new Date().toISOString() };
                pushPendingSystemMsg(zeile);
                setMessages(prev => trimMessages([...prev, zeile]));
                // Verlauf wieder vom Server holen: bei einem abgelehnten Wechsel hat der
                // Server nichts geraeumt, die Limit-Zeilen stehen also noch in der Datei.
                void pollNow();
              };

              // Save session for BOTH old and new account
              if (persistSession) {
                try { localStorage.setItem(getSessionKey(selectedId), sessionId); } catch {} // silent-ok: localStorage may be disabled
                try { localStorage.setItem(getSessionKey(newAcct), sessionId); } catch {} // silent-ok: localStorage may be disabled
              }
              setSelectedId(newAcct);
              // Keep sessionId, messages, and chat view — just switch account
              setMessages(prev => trimMessages([...prev, { role: 'system', content: `Kontowechsel angefragt → ${accountAccessLabel(newAcct)}`, timestamp: new Date().toISOString() }]));
              setAttention('idle');
              setAttentionReason(undefined);
              setRateLimitMessage(null);
              try { localStorage.setItem(storageKey, newAcct); } catch {} // silent-ok: localStorage may be disabled
              try { localStorage.setItem(kontoFuerSitzungKey(sid), newAcct); } catch {} // silent-ok: localStorage may be disabled
              // Reihenfolge: erst der optimistische Zustand, DANN die Anfrage. Der
              // Zweig "keine Verbindung" meldet sofort und synchron — stuende er davor,
              // wuerde React seine Ruecknahme im selben Takt wieder ueberschreiben.
              if (window.__cuiServerAlive !== false) {
                fetch(`/api/mission/conversation/${sid}/assign`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ accountId: newAcct }),
                  signal: AbortSignal.timeout(20000),
                }).then(async (res) => {
                  if (res.ok) {
                    const status = await res.json();
                    const actual = status.activeAccountId || status.assignedAccountId || prevAcct;
                    setSelectedId(actual);
                    try { localStorage.setItem(storageKey, actual); localStorage.setItem(kontoFuerSitzungKey(sid), actual); } catch {}
                    const text = status.status === 'pending' ? `Wechsel auf ${accountAccessLabel(status.effectiveAccountId)} nach dem laufenden Zug vorgemerkt. Noch aktiv: ${accountAccessLabel(status.activeAccountId)}.`
                      : status.status === 'blocked' ? `Kontowahl gespeichert, aber blockiert: ${status.reason}`
                      : status.status === 'ready' ? `${accountAccessLabel(status.effectiveAccountId)} ist für den nächsten Auftrag eingestellt. Aktuell läuft kein Prozess.`
                      : `Aktiv: ${accountAccessLabel(status.activeAccountId)}.`;
                    setMessages(prev => trimMessages([...prev, { role: 'system', content: text + (status.fallback ? ` Gewünschter Zugang nicht verfügbar: ${status.reason}. Ersatz: ${accountAccessLabel(status.effectiveAccountId)}.` : ''), timestamp: new Date().toISOString() }]));
                    if (status.status === 'pending' || status.status === 'blocked') return;
                    // Der Server hat die Limit-Zeilen aus der Datei geraeumt (unstick).
                    // Aus der ANZEIGE verschwinden sie damit nicht: der Verlauf bleibt beim
                    // Wechsel absichtlich stehen. Bis 19.09.2026 blieb die rote Zeile eines
                    // fremden, ausgelasteten Zugangs so in einem freien Fenster stehen.
                    setMessages(prev => prev.filter(m => m.role !== 'rate_limit' && m.role !== 'api_error'));
                    pendingSystemMsgsRef.current = pendingSystemMsgsRef.current.filter(
                      m => m.role !== 'rate_limit' && m.role !== 'api_error');
                    return;
                  }
                  let grund = `HTTP ${res.status}`;
                  try {
                    const body = await res.json();
                    if (body && typeof body.error === 'string') grund = body.error;
                  } catch {} // silent-ok: Fehlertext ist eine Zugabe, der Status steht schon fest
                  console.warn('[CuiLite] Assign abgelehnt:', res.status, grund);
                  meldeFehler(`Wechsel auf ${accountAccessLabel(newAcct)} abgelehnt — ${grund}. Der Chat laeuft weiter auf ${accountAccessLabel(prevAcct)}.`, true);
                }).catch((err) => {
                  // Netzfehler/Zeitlimit: der Ausgang ist UNBEKANNT, der Server kann den
                  // Wechsel sehr wohl vollzogen haben. Deshalb nicht zuruecknehmen (das
                  // waere die naechste Luege), sondern sichtbar als unbestaetigt melden.
                  console.warn('[CuiLite] Assign conversation error:', err);
                  meldeFehler(`Wechsel auf ${accountAccessLabel(newAcct)} nicht bestaetigt (${err?.name === 'TimeoutError' ? 'Zeitlimit' : 'Verbindungsfehler'}). Bitte die Seite neu laden und den angezeigten Zugang pruefen.`, false);
                });
              } else {
                meldeFehler(`Wechsel auf ${accountAccessLabel(newAcct)} nicht gesendet — keine Verbindung zum Server. Der Chat laeuft weiter auf ${accountAccessLabel(prevAcct)}.`, true);
              }
            } else {
              // No chat open (queue view) — normal switch
              let savedSession: string | null = null;
              try { savedSession = localStorage.getItem(getSessionKey(newAcct)); } catch {} // silent-ok: localStorage may be disabled
              setSelectedId(newAcct);
              setSessionId(savedSession);
              setShowQueue(!savedSession);
              setMessages([]);
              setAttention('idle');
              setAttentionReason(undefined);
              setRateLimitMessage(null);
              setLiveMode(false);
              try { localStorage.setItem(storageKey, newAcct); } catch {} // silent-ok: localStorage may be disabled
              if (savedSession) { try { localStorage.setItem(kontoFuerSitzungKey(savedSession), newAcct); } catch {} } // silent-ok: localStorage may be disabled
            }
          }}
          style={{
            background: 'var(--tn-bg)', color: 'var(--tn-text)',
            border: '1px solid var(--tn-border)', borderRadius: 4,
            padding: '2px 6px', fontSize: 11, cursor: 'pointer',
          }}
        >
          {SWITCHABLE_ACCOUNTS.map((a) => (
            <option key={a.id} value={a.id}>{a.label}</option>
          ))}
        </select>)}
        {/* Live mode toggle */}
        {sessionId && (
          <button data-ai-id="cui-lite-live-mode-toggle" onClick={() => setLiveMode(prev => !prev)} style={{
            background: liveMode ? 'rgba(168,85,247,0.15)' : 'none',
            border: `1px solid ${liveMode ? 'var(--tn-violett)' : 'var(--tn-border)'}`,
            color: liveMode ? 'var(--tn-violett)' : 'var(--tn-text-muted)',
            borderRadius: 4, padding: '1px 8px', fontSize: 10,
            cursor: 'pointer', fontWeight: liveMode ? 700 : 400,
            transition: 'all 0.2s',
            animation: liveMode ? 'live-pulse 2s ease-in-out infinite' : undefined,
            boxShadow: liveMode ? '0 0 8px rgba(168,85,247,0.3)' : 'none',
          }}>
            {liveMode ? '\u25CF Live' : '\u23F8 Static'}
          </button>
        )}

        {/* Manual refresh */}
        {sessionId && (
          <button data-ai-id="cui-lite-refresh" onClick={() => { lastPollHashRef.current = ''; pollNow(); }} style={{
            background: 'var(--tn-bg)', color: 'var(--tn-text-muted)',
            border: '1px solid var(--tn-border)', borderRadius: 4,
            padding: '1px 8px', fontSize: 10, cursor: 'pointer',
          }}>
            Refresh
          </button>
        )}

        {/* Review button — starts independent review session */}
        {sessionId && (
          <button data-ai-id="cui-lite-review" onClick={handleReview} disabled={reviewState === 'running'}
            title="Unabhängige Review-Session starten — prüft ob Umsetzung den Anforderungen entspricht"
            style={{
              background: reviewState === 'done' ? 'rgba(158,206,106,0.15)' : reviewState === 'running' ? 'rgba(122,162,247,0.1)' : 'transparent',
              color: reviewState === 'done' ? 'var(--tn-green)' : reviewState === 'running' ? 'var(--tn-link)' : 'var(--tn-text-muted)',
              border: `1px solid ${reviewState === 'done' ? 'var(--tn-green)' : reviewState === 'running' ? 'var(--tn-link)' : 'var(--tn-border)'}`,
              borderRadius: 4, padding: '1px 8px', fontSize: 10, cursor: reviewState === 'running' ? 'default' : 'pointer', fontWeight: 600,
              opacity: reviewState === 'running' ? 0.8 : 1,
            }}>
            {reviewState === 'running' ? '⧖ Review...' : reviewState === 'done' ? '✓ Review' : 'Review'}
          </button>
        )}

        {/* Dim-pin — keep a busy panel bright (overrides auto-grey while Claude works) */}
        {sessionId && busyOrBlocked && (
          <button data-ai-id="cui-lite-dim-pin" onClick={toggleDimPin} title={dimPinned ? 'Hell-Fixierung aufheben' : 'Panel hell halten (auch während Claude arbeitet)'}
            style={{
              background: dimPinned ? 'rgba(158,206,106,0.15)' : 'transparent',
              color: dimPinned ? 'var(--tn-green)' : 'var(--tn-text-muted)',
              border: `1px solid ${dimPinned ? 'var(--tn-green)' : 'var(--tn-border)'}`,
              borderRadius: 4, padding: '1px 6px', fontSize: 10, cursor: 'pointer', fontWeight: 600,
            }}>
            📌
          </button>
        )}

        {/* Pause button — suppresses needs_attention without closing */}
        {sessionId && (
          <button data-ai-id="cui-lite-pause" onClick={handlePause} title={isPaused ? 'Pausierung aufheben' : 'Konversation pausieren'}
            style={{
              background: isPaused ? 'rgba(122,162,247,0.15)' : 'transparent',
              color: isPaused ? 'var(--tn-link)' : 'var(--tn-text-muted)',
              border: `1px solid ${isPaused ? 'var(--tn-link)' : 'var(--tn-border)'}`,
              borderRadius: 4, padding: '1px 8px', fontSize: 10, cursor: 'pointer', fontWeight: 600,
              boxShadow: isPaused ? '0 0 6px rgba(122,162,247,0.25)' : 'none',
            }}>
            ⏸ {isPaused ? 'Pausiert' : 'Pause'}
          </button>
        )}

        {/* Finish button — persistent close */}
        {sessionId && (
          <button data-ai-id="cui-lite-finish" onClick={(e) => { e.stopPropagation(); handleFinish(); }} title="Konversation abschließen"
            style={{
              background: 'transparent', color: 'var(--tn-fehler)',
              border: '1px solid var(--tn-fehler)', borderRadius: 4,
              padding: '1px 6px', fontSize: 10, cursor: 'pointer', fontWeight: 600,
            }}>
            Finish
          </button>
        )}

        {/* Model badge */}
        {sessionId && sessionModel && (
          <span style={{
            fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3,
            background: sessionModel === 'opus' ? 'var(--tnfh-2d2040)' : sessionModel === 'fable' ? 'var(--tnfh-1a3020)' : 'var(--tn-bg)',
            color: sessionModel === 'opus' ? 'var(--tn-purple)' : sessionModel === 'fable' ? 'var(--tnf-7ec699)' : 'var(--tn-text-muted)',
            border: `1px solid ${sessionModel === 'opus' ? 'var(--tn-purple)' : 'var(--tn-border)'}`,
            textTransform: 'uppercase', letterSpacing: '0.5px',
          }}>
            {sessionModel}
          </span>
        )}

            {sessionId && !showQueue && messages.length > 0 && (
              <button
                data-ai-id="cui-lite-tools-toggle-all"
                onClick={toggleExpandTools}
                title={expandTools ? 'Tool-Details einklappen' : 'Tool-Details ausklappen'}
                style={{ padding: '2px 8px', fontSize: 10, background: expandTools ? 'var(--tn-bg-highlight)' : 'transparent', border: '1px solid var(--tn-border)', borderRadius: 3, color: 'var(--tn-text-muted)', cursor: 'pointer' }}
              >
                {expandTools ? '▲ Tools' : '▼ Tools'}
              </button>
            )}
          </div>
          {sessionId && !showQueue && (<>
            {/* Action Buttons Row - Plan, Loop, Stop, ... */}
            {/* Fachpartner role: only Stop visible (Plan/Loop/KILL/Templates are dev-tools). */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', marginBottom: 4 }}>
              {isAdvancedUser && (
                <button
                  data-ai-id="cui-lite-plan-toggle"
                  onClick={() => setPlanMode(!planMode)}
                  title={`Plan-Modus: ${accountAssistantLabel(selectedId)} plant zuerst`}
                  style={{
                    padding: '4px 8px', borderRadius: 4, cursor: 'pointer', flexShrink: 0,
                    background: planMode ? 'rgba(245,158,11,0.15)' : 'var(--tn-bg)',
                    border: `1px solid ${planMode ? 'var(--tn-warn)' : 'var(--tn-border)'}`,
                    color: planMode ? 'var(--tn-warn)' : 'var(--tn-text-muted)',
                    fontSize: 11, fontWeight: planMode ? 700 : 400,
                  }}
                >
                  Plan
                </button>
              )}
              {/* Loop = Auto-Inject, Route admin-only (autoinject.ts, seit c33e83f28). Product Owner
                * sahen den Knopf bis 03.10.2026 trotzdem und bekamen beim Klick 403 — gezeigt wird er
                * jetzt mit derselben Rolle, mit der die Route entscheidet (`betreiberWege`, b99349ead). */}
              {betreiberWege && (
                <button
                  data-ai-id="cui-lite-loop-toggle"
                  onClick={() => { if (loopEnabled) { toggleLoop(false); } else { toggleLoop(true); } }}
                  onContextMenu={(e) => { e.preventDefault(); setShowLoopConfig(!showLoopConfig); }}
                  title={loopEnabled ? "Loop stoppen (Klick)" : "Loop starten (Klick) | Config (Rechtsklick)"}
                  style={{
                    padding: "4px 8px", borderRadius: 4, cursor: "pointer", flexShrink: 0,
                    background: loopEnabled ? "rgba(16,185,129,0.15)" : "var(--tn-bg)",
                    border: `1px solid ${loopEnabled ? "var(--tn-ok)" : "var(--tn-border)"}`,
                    color: loopEnabled ? "var(--tn-ok)" : "var(--tn-text-muted)",
                    fontSize: 11, fontWeight: loopEnabled ? 700 : 400,
                  }}
                >
                  {loopEnabled ? "Loop \u25CF" : "Loop"}
                </button>
              )}
              {/* Steht der Stopp schon im Kopf (Sitzung arbeitet), hier nicht doppelt. */}
              {!busyOrBlocked && <button
                data-ai-id="cui-lite-stop"
                onClick={handleStop}
                title="Konversation stoppen"
                style={{
                  padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer', flexShrink: 0,
                  background: convStatus === 'ongoing' ? 'var(--tn-fehler)' : 'var(--tn-bg)',
                  border: `1px solid ${convStatus === 'ongoing' ? 'var(--tn-fehler)' : 'var(--tn-border)'}`,
                  color: convStatus === 'ongoing' ? '#fff' : 'var(--tn-text-muted)',
                  fontWeight: 600,
                }}
              >
                Stop
              </button>}
              {isAdvancedUser && (
                <button
                  data-ai-id="cui-lite-hard-kill"
                  onClick={handleHardKill}
                  title="HARD KILL — Alle Prozesse sofort beenden (inkl. Zombies)"
                  style={{
                    padding: '4px 6px', borderRadius: 4, fontSize: 9, cursor: 'pointer', flexShrink: 0,
                    background: 'rgba(239,68,68,0.15)',
                    border: '1px solid color-mix(in srgb, var(--tnf-ef4444) 50%, transparent)',
                    color: 'var(--tn-fehler)', fontWeight: 700, letterSpacing: 0.5,
                  }}
                >
                  KILL
                </button>
              )}
              {isAdvancedUser && (
                <button
                  data-ai-id="cui-lite-template-new"
                  onClick={() => { setShowTemplateForm(true); setEditingTemplate(null); setNewTplLabel(''); setNewTplMessage(''); }}
                  title="Neuen Kurzprompt anlegen"
                  style={{ padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer', background: 'transparent', border: '1px dashed var(--tn-border)', color: 'var(--tn-text-muted)' }}
                >
                  + Kurzprompt
                </button>
              )}
              {planMode && isAdvancedUser && (
                <span style={{ fontSize: 10, color: 'var(--tn-warn)', fontWeight: 600, marginLeft: 4 }}>
                  Plan-Modus
                </span>
              )}
            </div>
            {/* Loop Config Panel */}
            {showLoopConfig && betreiberWege && (
              <div style={{ marginBottom: 4, padding: 6, background: "var(--tn-bg)", border: "1px solid var(--tn-ok)", borderRadius: 4 }}>
                <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 10, color: "var(--tn-ok)", fontWeight: 600 }}>Loop Config</span>
                  <label style={{ fontSize: 10, color: "var(--tn-text-muted)" }}>Interval:</label>
                  <select data-ai-id="cui-lite-loop-interval" value={loopIntervalMin} onChange={e => setLoopIntervalMin(Number(e.target.value))} style={{ padding: "2px 4px", fontSize: 10, background: "var(--tn-bg-dark)", color: "var(--tn-text)", border: "1px solid var(--tn-border)", borderRadius: 3 }}>
                    <option value={1}>1 min</option>
                    <option value={2}>2 min</option>
                    <option value={3}>3 min</option>
                    <option value={5}>5 min</option>
                    <option value={10}>10 min</option>
                    <option value={15}>15 min</option>
                  </select>
                  <div style={{ flex: 1 }} />
                  <button data-ai-id="cui-lite-loop-start" onClick={() => { toggleLoop(true); setShowLoopConfig(false); }} style={{ padding: "3px 10px", borderRadius: 3, fontSize: 10, cursor: "pointer", background: "var(--tn-ok)", border: "none", color: "#fff", fontWeight: 600 }}>Start</button>
                  <button data-ai-id="cui-lite-loop-cancel" onClick={() => setShowLoopConfig(false)} style={{ padding: "3px 6px", borderRadius: 3, fontSize: 10, cursor: "pointer", background: "transparent", border: "1px solid var(--tn-border)", color: "var(--tn-text-muted)" }}>X</button>
                </div>
                <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                  {LOOP_PRESETS.map((preset, i) => (
                    <button key={i} data-ai-id={`cui-lite-loop-preset-${i}`} onClick={() => setLoopMessage(preset.message)} title={preset.message.slice(0, 120)} style={{ padding: "2px 6px", borderRadius: 3, fontSize: 9, cursor: "pointer", background: loopMessage === preset.message ? "rgba(16,185,129,0.2)" : "var(--tn-bg-dark)", border: `1px solid ${loopMessage === preset.message ? "var(--tn-ok)" : "var(--tn-border)"}`, color: loopMessage === preset.message ? "var(--tn-ok)" : "var(--tn-text-muted)", fontWeight: loopMessage === preset.message ? 600 : 400 }}>
                      {preset.label}
                    </button>
                  ))}
                </div>
                <textarea data-ai-id="cui-lite-loop-message" value={loopMessage} onChange={e => setLoopMessage(e.target.value)} placeholder="Auto-Inject Nachricht..." rows={2} style={{ width: "100%", padding: "4px 6px", fontSize: 10, background: "var(--tn-bg-dark)", color: "var(--tn-text)", border: "1px solid var(--tn-border)", borderRadius: 3, fontFamily: "inherit", resize: "vertical", minHeight: 36, boxSizing: "border-box", lineHeight: "1.3" }} />
                {lastInjectTime && <div style={{ fontSize: 9, color: "var(--tn-text-muted)", marginTop: 2 }}>Letzter Inject: {new Date(lastInjectTime).toLocaleTimeString("de-DE")}</div>}
              </div>
            )}
            {/* Prompt Template Cards */}
            {replyTemplates.length > 0 && !showTemplateForm && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4, maxHeight: 80, overflowY: 'auto' }}>
                {replyTemplates.map(tpl => (
                  <div key={tpl.id} style={{ display: 'flex', alignItems: 'stretch', borderRadius: 4, border: '1px solid var(--tn-border)', overflow: 'hidden', maxWidth: '48%' }}>
                    <button
                      data-ai-id={`cui-lite-template-send-${tpl.id}`}
                      onClick={() => handleSend(tpl.message)}
                      title={tpl.message}
                      style={{ padding: '4px 8px', fontSize: 10, cursor: 'pointer', background: 'var(--tn-bg)', border: 'none', color: 'var(--tn-text)', textAlign: 'left', fontFamily: 'inherit', lineHeight: '1.3', flex: 1, minWidth: 0 }}
                    >
                      <div style={{ fontWeight: 600, color: 'var(--tn-text)', marginBottom: 1 }}>{tpl.label}</div>
                      <div style={{ color: 'var(--tn-text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{tpl.message.slice(0, 60)}{tpl.message.length > 60 ? '...' : ''}</div>
                    </button>
                    <button
                      data-ai-id={`cui-lite-template-edit-${tpl.id}`}
                      onClick={() => { setEditingTemplate(tpl); setNewTplLabel(tpl.label); setNewTplMessage(tpl.message); setShowTemplateForm(true); }}
                      title="Bearbeiten"
                      style={{ padding: '2px 5px', fontSize: 9, cursor: 'pointer', background: 'var(--tn-bg-dark)', border: 'none', borderLeft: '1px solid var(--tn-border)', color: 'var(--tn-text-muted)', fontFamily: 'inherit', flexShrink: 0, display: 'flex', alignItems: 'center' }}
                    >
                      &#9998;
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Template Form */}
            {showTemplateForm && (
              <div style={{ marginBottom: 4, padding: 6, background: 'var(--tn-bg)', border: '1px solid var(--tn-blue)', borderRadius: 4 }}>
                <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                  <input data-ai-id="cui-lite-template-label" value={newTplLabel} onChange={e => setNewTplLabel(e.target.value)} placeholder="Label (kurz)" style={{ width: 100, padding: '4px 6px', fontSize: 11, background: 'var(--tn-bg-dark)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)', borderRadius: 3, fontFamily: 'inherit' }} />
                  <div style={{ flex: 1 }} />
                  <button data-ai-id="cui-lite-template-save" onClick={handleSaveTemplate} disabled={!newTplLabel.trim() || !newTplMessage.trim()} style={{ padding: '4px 10px', borderRadius: 3, fontSize: 11, cursor: 'pointer', background: newTplLabel.trim() && newTplMessage.trim() ? 'var(--tn-blue)' : 'var(--tn-border)', border: 'none', color: '#fff', fontWeight: 600 }}>{editingTemplate ? 'Update' : 'Speichern'}</button>
                  {editingTemplate && <button data-ai-id="cui-lite-template-delete" onClick={() => { if (confirm(`"${editingTemplate.label}" löschen?`)) { handleDeleteTemplate(editingTemplate.id); setShowTemplateForm(false); setEditingTemplate(null); } }} style={{ padding: '4px 8px', borderRadius: 3, fontSize: 11, cursor: 'pointer', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: 'var(--tn-fehler)', fontWeight: 600 }}>Löschen</button>}
                  <button data-ai-id="cui-lite-template-cancel" onClick={() => { setShowTemplateForm(false); setEditingTemplate(null); }} style={{ padding: '4px 8px', borderRadius: 3, fontSize: 11, cursor: 'pointer', background: 'transparent', border: '1px solid var(--tn-border)', color: 'var(--tn-text-muted)' }}>X</button>
                </div>
                <textarea data-ai-id="cui-lite-template-message" value={newTplMessage} onChange={e => setNewTplMessage(e.target.value)} placeholder="Prompt-Text eingeben..." rows={3} onKeyDown={e => { if (e.key === 'Escape') { setShowTemplateForm(false); setEditingTemplate(null); } }} style={{ width: '100%', padding: '4px 6px', fontSize: 11, background: 'var(--tn-bg-dark)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)', borderRadius: 3, fontFamily: 'inherit', resize: 'vertical', minHeight: 50, boxSizing: 'border-box', lineHeight: '1.4' }} />
              </div>
            )}
          </>)}
          </>)}
        </div>
      )}

      {/* Attention Banner */}
      {attention === 'needs_attention' && sessionId && attentionReason !== 'done' && (
        <div style={{
          padding: '6px 16px',
          background: attentionReason === 'plan' ? 'rgba(245,158,11,0.12)'
            : attentionReason === 'question' ? 'rgba(59,130,246,0.12)'
            : attentionReason === 'rate_limit' ? 'rgba(239,68,68,0.08)'
            : attentionReason === 'context_overflow' ? 'rgba(224,175,104,0.12)'
            : 'rgba(239,68,68,0.12)',
          borderBottom: '1px solid var(--tn-border)',
          display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
        }}>
          <span style={{
            fontSize: 12, fontWeight: 700,
            color: attentionReason === 'plan' ? 'var(--tn-orange-hell)'
              : attentionReason === 'question' ? 'var(--tn-orange-hell)'
              : attentionReason === 'context_overflow' ? 'var(--tn-orange-hell)'
              : attentionReason === 'rate_limit' ? 'var(--tn-fehler)'
              : 'var(--tn-fehler)',
          }}>
            {attentionReason === 'plan' ? 'Plan wartet auf Freigabe'
              : attentionReason === 'question' ? `${accountAssistantLabel(selectedId)} hat eine Frage`
              : attentionReason === 'context_overflow' ? 'Kontext zu lang — Nachricht erneut senden, wird automatisch kompaktiert.'
              : attentionReason === 'rate_limit' ? 'Kontingent ausgelastet — anderen KI-Zugang waehlen oder auf die Freigabe warten.'
              : (attentionReason === 'error' || attentionReason === 'send_failed') ? (rateLimitMessage || 'Nachricht konnte nicht zugestellt werden. Bitte erneut versuchen.')
              : 'Aktion erforderlich'}
          </span>
          <span style={{ flex: 1 }} />
          <button data-ai-id="cui-lite-attention-refresh" onClick={() => { lastPollHashRef.current = ''; pollNow(); }} style={{
            padding: '2px 10px', fontSize: 11, borderRadius: 3, cursor: 'pointer',
            background: 'var(--tn-bg)', border: '1px solid var(--tn-border)', color: 'var(--tn-text)',
          }}>
            Aktualisieren
          </button>
        </div>
      )}

      {buehnenFehler && (
        <div data-ai-id="cui-lite-buehnen-fehler" role="alert" style={{ padding: '6px 16px', display: 'flex', gap: 8, alignItems: 'center',
          borderBottom: '1px solid var(--tn-border)', fontSize: 12, color: 'var(--tn-fehler)' }}>
          <span>Chat läuft, aber seine Bühne wurde nicht angelegt: {buehnenFehler} — er steht im Guide unter „ohne Platz“.</span>
          <span style={{ flex: 1 }} />
          <button type="button" onClick={() => setBuehnenFehler(null)} style={{ fontSize: 11, padding: '2px 8px' }}>verstanden</button>
        </div>
      )}

      {/* Permission Bar */}
      {permissions.length > 0 && sessionId && (
        <div style={{
          padding: '6px 16px',
          background: 'rgba(245,158,11,0.08)',
          borderBottom: '1px solid var(--tn-border)',
          flexShrink: 0,
          maxHeight: '60vh', overflow: 'auto',
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tn-warn)', marginBottom: 4 }}>
            Genehmigungen ({permissions.length})
          </div>
          {permissions.map(perm => {
            const planText = perm.toolInput?.plan as string | undefined;
            const isPlanMode = perm.toolName === 'ExitPlanMode' || perm.toolName === 'EnterPlanMode';
            return (
              <div key={perm.id} style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: 'var(--tn-text)', flex: 1 }}>
                    {perm.toolName || perm.type}: {perm.title || perm.id.slice(0, 8)}
                  </span>
                  <button data-ai-id={`cui-lite-permission-approve-${perm.id}`} onClick={() => handlePermission(perm.id, 'approve')} style={{
                    padding: '3px 10px', fontSize: 11, borderRadius: 3, cursor: 'pointer',
                    background: 'var(--tn-ok)', border: 'none', color: '#fff', fontWeight: 600,
                  }}>OK</button>
                  <button data-ai-id={`cui-lite-permission-deny-${perm.id}`} onClick={() => handlePermission(perm.id, 'deny')} style={{
                    padding: '3px 10px', fontSize: 11, borderRadius: 3, cursor: 'pointer',
                    background: 'var(--tn-fehler)', border: 'none', color: '#fff', fontWeight: 600,
                  }}>X</button>
                </div>
                {isPlanMode && planText && (
                  <details style={{ marginTop: 4 }}>
                    <summary style={{ fontSize: 11, color: 'var(--tn-warn)', cursor: 'pointer' }}>
                      Plan anzeigen ({planText.length > 1000 ? `${Math.round(planText.length / 1000)}k Zeichen` : `${planText.length} Zeichen`})
                    </summary>
                    <div style={{
                      marginTop: 4, padding: 8, borderRadius: 4,
                      background: 'var(--tn-bg)', border: '1px solid var(--tn-border)',
                      fontSize: 11, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                      maxHeight: 300, overflow: 'auto', color: 'var(--tn-text)',
                    }}>
                      {planText}
                    </div>
                  </details>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Content: Queue or Messages */}
      {showQueue || !sessionId ? (
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <QueueOverlay
            accountId={selectedId}
            projectId={projectId}
            workDir={workDir}
            onNavigate={handleQueueNavigate}
            onStartNew={handleStartNew}
            refreshSignal={queueRefresh}
            simple={simpleMode}
          />
        </div>
      ) : (
        <>
          {/* Message List */}
          <div
            ref={scrollContainerRef}
            onScroll={() => {
              const el = scrollContainerRef.current;
              if (!el) return;
              // "Near bottom" = within 150px of the end (80px was too tight, missed scroll)
              const isScrolledUp = el.scrollTop + el.clientHeight < el.scrollHeight - 150;
              userScrolledUpRef.current = isScrolledUp;
              if (!isScrolledUp) setUnreadCount(0);
            }}
            style={{ flex: 1, overflow: 'auto', minHeight: 0 }}
          >
            {messages.length === 0 && (
              <LoadingConversation sessionId={sessionId} onBack={() => { if (sessionId) onLoadFailed?.(sessionId); }} onRetry={pollNow} onLoadFailed={onLoadFailed} />
            )}
            {messages.length > 15 && !showAllMessages && (
              <button data-ai-id="cui-lite-load-older" onClick={() => setShowAllMessages(true)} style={{
                display: 'block', width: '100%', padding: '8px', background: 'var(--tn-bg-highlight)',
                border: 'none', color: 'var(--tn-blue)', cursor: 'pointer', fontSize: 11,
              }}>
                {messages.length - 15} aeltere Nachrichten laden...
              </button>
            )}
            {(() => {
              const visible = showAllMessages ? messages : messages.slice(-15);
              const lastUnansweredIdx = findLastUnansweredQuestionIdx(visible);
              return visible.map((msg, i, arr) => (
                <MessageRow
                  key={msg.timestamp || i}
                  rowId={String(msg.timestamp || i)}
                  msg={msg}
                  onRespond={handleRespond}
                  isLast={i === arr.length - 1}
                  forceInteractive={i === lastUnansweredIdx}
                  workDir={workDir}
                  selectedId={selectedId}
                  serverPlanText={serverPlanText}
                  sessionCwd={sessionCwd}
                  expandTools={expandTools}
                />
              ));
            })()}
            <div ref={messagesEndRef} />
          </div>

          {/* New-messages indicator — shown when user scrolled up and new messages arrived */}
          {unreadCount > 0 && (
            <div style={{ position: 'relative', flexShrink: 0, height: 0 }}>
              <button
                data-ai-id="cui-lite-scroll-to-new"
                onClick={() => {
                  scrollContainerRef.current?.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' });
                  userScrolledUpRef.current = false;
                  setUnreadCount(0);
                }}
                style={{
                  position: 'absolute', bottom: 8, right: 12,
                  background: 'var(--tn-blue, var(--tn-info))', color: '#fff',
                  border: 'none', borderRadius: 16, padding: '5px 14px',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.35)', zIndex: 10,
                }}
              >
                ↓ {unreadCount} neue Nachricht{unreadCount !== 1 ? 'en' : ''}
              </button>
            </div>
          )}

          {/* Input Bar */}
          <div style={{
            padding: '8px 12px', borderTop: '1px solid var(--tn-border)',
            background: 'var(--tn-bg-dark)', flexShrink: 0,
          }}>
            {/* Drop error — fail loud, never silent */}
            {dropError && (
              <div
                data-ai-id="cui-lite-drop-error-dismiss"
                onClick={() => setDropError('')}
                title="Klicken zum Ausblenden"
                style={{ marginBottom: 4, padding: '4px 8px', fontSize: 10, background: 'rgba(239,68,68,0.1)', color: 'var(--tn-fehler)', borderRadius: 4, cursor: 'pointer' }}
              >
                {dropError}
              </div>
            )}
            {pendingSendCount > 0 && <div role="status" data-ai-id="cui-lite-pending-send" style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginBottom: 4 }}>
              {pendingSendCount} {pendingSendCount === 1 ? 'Nachricht wird' : 'Nachrichten werden'} noch übertragen …
            </div>}
            {/* Input Row - Paste Zone + Textarea + Send. The whole row is a drop zone for files. */}
            <div
              onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setFileDragOver(true); } }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setFileDragOver(false); }}
              onDrop={handleFileDrop}
              style={{
                display: 'flex', gap: 6, alignItems: 'flex-end',
                borderRadius: 4,
                outline: fileDragOver ? '2px dashed var(--tn-info)' : 'none',
                outlineOffset: 2,
                background: fileDragOver ? 'rgba(59,130,246,0.06)' : 'transparent',
                transition: 'background 0.15s',
              }}
            >
              {/* Attach button (📎) — opens the OS file picker. Reuses the same
                  upload path as drag&drop. This is the "Heftklammer" partners looked for. */}
              <input
                data-ai-id="cui-lite-file-input"
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFilePicked}
                style={{ display: 'none' }}
              />
              <button
                data-ai-id="cui-lite-attach"
                type="button"
                onClick={handleAttachClick}
                disabled={fileUploading}
                title="Dateien anhängen (PDF, Excel, Bilder …) — auch mehrere"
                style={{
                  width: 36, minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: fileUploading ? 'rgba(59,130,246,0.15)' : 'var(--tn-bg)',
                  border: `1px solid ${fileUploading ? 'var(--tn-info)' : 'var(--tn-border)'}`,
                  borderRadius: 4, cursor: fileUploading ? 'default' : 'pointer', outline: 'none',
                  fontSize: 16, color: fileUploading ? 'var(--tn-info)' : 'var(--tn-text-muted)',
                  flexShrink: 0,
                }}
              >
                {fileUploading ? '⏳' : '📎'}
              </button>
              <BildEinfuegeZone
                key={selectedId}
                aiId="cui-lite-paste-zone"
                onBusy={setPasteUploading}
                onText={command => {
                  setInput(prev => (prev ? prev + '\n' : '') + command);
                  textareaRef.current?.focus();
                }}
              />
              {/* Voice input (\uD83C\uDFA4) \u2014 Whisper via AI Bridge. Transcript is appended
                  to the composer so you can edit before sending. */}
              {!kompakt && !simpleMode && <VoiceMicButton
                disabled={isLoading}
                onTranscript={(t) => {
                  setInput((prev) => (prev ? prev.replace(/\s+$/, '') + ' ' + t : t));
                  textareaRef.current?.focus();
                }}
              />}
              <textarea
                data-ai-id="cui-lite-message"
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    // Tasten-Repeat einer gehaltenen Enter-Taste feuert Keydowns im
                    // ~40ms-Takt — nur der ERSTE Anschlag darf senden. (Ursache des
                    // Doppel-Fork-Vorfalls vom 25.08.2026, zusammen mit dem
                    // sendInFlightRef-Guard in handleSend.)
                    if (e.repeat) return;
                    handleSend();
                  }
                }}
                placeholder={fileUploading ? 'Dateien werden hochgeladen...' : fileDragOver ? 'Dateien hier ablegen...' : pasteUploading ? 'Bild wird hochgeladen...' : planMode ? 'Aufgabe beschreiben... (Plan-Modus)' : 'Nachricht... (Enter = Senden · 📎 oder Drag&Drop für Dateien)'}
                rows={2}
                style={{
                  flex: 1, resize: 'vertical', padding: '6px 10px', fontSize: 13,
                  background: 'var(--tn-bg)', color: 'var(--tn-text)',
                  border: `1px solid ${pasteUploading ? 'var(--tn-violett)' : planMode ? 'var(--tn-warn)' : 'var(--tn-border)'}`, borderRadius: 4,
                  fontFamily: 'inherit', maxHeight: 120, minHeight: 48,
                }}
              />
              <button
                data-ai-id="cui-lite-send"
                onClick={() => handleSend()}
                // NUR am Text haengen, nicht an isLoading: ein Send waehrend die
                // Sitzung/das Panel beschaeftigt ist, muss instant angenommen und
                // sichtbar gereiht werden (Rafael, 12.09.2026), nicht durch einen
                // deaktivierten Button verhindert werden — handleSend queued selbst,
                // wenn sendInFlightRef schon besetzt ist.
                disabled={!input.trim()}
                style={{
                  padding: '6px 14px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                  background: input.trim() ? (planMode ? 'var(--tn-orange-hell)' : 'var(--tn-green)') : 'var(--tn-border)',
                  border: 'none', color: input.trim() ? '#fff' : 'var(--tn-knopf-aus-text)', fontWeight: 600,
                  opacity: input.trim() ? 1 : 0.5,
                  alignSelf: 'stretch',
                }}
              >
                {isLoading ? '⏳' : planMode ? 'Planen' : 'Senden'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
